import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, TrendingUp, Signal, Settings, Play, Pause,
  Download, Search, Bell, BellOff, Volume2, VolumeX,
  RefreshCw, Eye, EyeOff, History, Wallet, Zap, Sun, Moon,
  BookOpen, Shield, AlertTriangle, X, DollarSign, Dumbbell,
  BarChart3, PieChart, Calendar, Clock, Target, Award, Flame
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart as RechartsPieChart, Pie, Cell,
  RadialBarChart, RadialBar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { motion } from 'framer-motion';
import apiService from './apiService';
import EquityCurve from './components/EquityCurve';
import TradingViewChart from './components/TradingViewChart';
import AnimatedNumber from './components/AnimatedNumber';
import { SkeletonCard, SkeletonRow } from './components/Skeleton';
import { useAuth } from './auth/AuthContext';
import { bodyFont, displayFont, getIriSoft, getTokens, iri, monoFont } from './theme/tokens';

/* Logo */
const FallingStarLogo = ({ size = 32 }) => (
  <img src="https://iili.io/BWRRmBf.png" alt="xceller." width={size} height={size} style={{ objectFit: 'contain', pointerEvents: 'none', userSelect: 'none' }} />
);

export default function DashboardApp() {
  const today = new Date().toISOString().slice(0, 10);
  const { user } = useAuth();
  const isAdmin = user?.is_admin === true;
  const [isRunning, setIsRunning] = useState(false);
  const [signals, setSignals] = useState([]);
  const [config, setConfig] = useState({
    telegram: { apiId: '', apiHash: '', channelName: '', channelNames: [''], channelProfiles: [] },
    mt5: { login: '', password: '', server: '' },
    trading: {
      lotSize: '0.01',
      autoBreakeven: true,
      stopLossMode: 'signal',
      riskPerTrade: '2',
      maxDailyLoss: '100',
      maxPositions: '20',
      trailingStop: false,
      tpOrders: { tp1: 1, tp2: 1, tp3: 1 },
      maxTpPips: '',
      tpCapIncrement: '50',
    }
  });
  const [stats, setStats] = useState({ total_signals: 0, executed: 0, profit: 0.0, win_rate: 0.0 });
  const [account, setAccount] = useState({ balance: 0.0, equity: 0.0, profit: 0.0, currency: 'USD', server: '' });
  const [tradeHistory, setTradeHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  // Chart starts collapsed — the iframe is heavy and most of the time the user
  // just wants the dashboard stats. Persist the user's preference across reloads.
  const [chartExpanded, setChartExpanded] = useState(() => {
    try { return localStorage.getItem('xceller_chart_expanded') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('xceller_chart_expanded', chartExpanded ? '1' : '0'); } catch {}
  }, [chartExpanded]);
  const [notifications, setNotifications] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [showPasswordFields, setShowPasswordFields] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterPnL, setFilterPnL] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('time');
  const [autoRefresh] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState({ telegram: false, mt5: false });
  const [isLoading, setIsLoading] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('xceller_theme') || 'dark');
  const [themeTransition, setThemeTransition] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [sessionStart, setSessionStart] = useState(null);
  const [sessionElapsed, setSessionElapsed] = useState('');
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [journalEntries, setJournalEntries] = useState(() => {
    try { return JSON.parse(localStorage.getItem('xceller_journal') || '[]'); } catch { return []; }
  });
  const [journalDraft, setJournalDraft] = useState({ pair: '', direction: 'BUY', notes: '', emotion: 'neutral', outcome: '' });
  const [auditDate, setAuditDate] = useState(today);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [telegramChats, setTelegramChats] = useState([]);
  const [telegramChatsLoading, setTelegramChatsLoading] = useState(false);
  const [positions, setPositions] = useState([]);
  const [goldPrice, setGoldPrice] = useState(null);
  const [closingTickets, setClosingTickets] = useState(new Set());
  const [dailyRecap, setDailyRecap] = useState(null);
  const [recapLoading, setRecapLoading] = useState(false);
  const dismissedIds = React.useRef(new Set());

  const isDark = theme === 'dark';

  useEffect(() => { loadConfig(); loadHistory(); }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const stopPolling = apiService.startPolling((data) => {
      if (data.error) return;
      if (data.status) {
        setIsRunning(data.status.running);
        setConnectionStatus({ telegram: data.status.telegram_connected, mt5: data.status.mt5_connected });
      }
      if (data.stats) setStats(data.stats);
      if (data.signals) setSignals(data.signals);
      if (data.account) setAccount(data.account);
      if (data.positions !== undefined) setPositions(data.positions || []);
      if (data.goldPrice !== undefined) setGoldPrice(data.goldPrice);
      if (!initialLoaded) setInitialLoaded(true);
      if (data.notifications) {
        setNotifications(prev => {
          const incoming = data.notifications.filter(n => !dismissedIds.current.has(n.id));
          incoming.forEach(n => {
            if (!prev.some(p => p.id === n.id)) {
              setTimeout(() => {
                dismissedIds.current.add(n.id);
                setNotifications(cur => cur.filter(x => x.id !== n.id));
              }, 4000);
            }
          });
          const prevIds = new Set(incoming.map(n => n.id));
          const kept = prev.filter(n => !prevIds.has(n.id) && !dismissedIds.current.has(n.id));
          return [...incoming, ...kept].slice(0, 10);
        });
      }
    }, 1000);
    return () => stopPolling();
  }, [autoRefresh]);

  const loadConfig = async () => {
    try {
      const apiConfig = await apiService.getConfig();
      if (apiConfig) {
        setConfig({
          telegram: {
            apiId: apiConfig.telegram?.api_id || '',
            apiHash: apiConfig.telegram?.api_hash || '',
            channelName: apiConfig.telegram?.channel_name || '',
            channelNames: apiConfig.telegram?.channel_names?.length ? apiConfig.telegram.channel_names : [apiConfig.telegram?.channel_name || ''],
            channelProfiles: Array.isArray(apiConfig.telegram?.channel_profiles) ? apiConfig.telegram.channel_profiles : [],
          },
          mt5: { login: apiConfig.mt5?.login || '', password: apiConfig.mt5?.password || '', server: apiConfig.mt5?.server || '' },
          trading: {
            lotSize: apiConfig.trading?.lot_size || '0.01',
            autoBreakeven: apiConfig.trading?.auto_breakeven !== false,
            stopLossMode: apiConfig.trading?.stop_loss_mode || 'signal',
            riskPerTrade: String(apiConfig.trading?.risk_per_trade ?? '2'),
            maxDailyLoss: String(apiConfig.trading?.max_daily_loss ?? '100'),
            maxPositions: String(apiConfig.trading?.max_positions ?? '20'),
            trailingStop: apiConfig.trading?.trailing_stop === true,
            maxTpPips: apiConfig.trading?.max_tp_pips != null ? String(apiConfig.trading.max_tp_pips) : '',
            tpCapIncrement: apiConfig.trading?.tp_cap_increment != null ? String(apiConfig.trading.tp_cap_increment) : '50',
            tpOrders: {
              tp1: apiConfig.trading?.tp_orders?.tp1 || 1,
              tp2: apiConfig.trading?.tp_orders?.tp2 || 1,
              tp3: apiConfig.trading?.tp_orders?.tp3 || 1,
            }
          }
        });
      }
    } catch (e) { console.error(e); }
  };

  const addNotification = (message, type = 'info') => {
    if (!notificationsEnabled) return;
    const id = Date.now();
    setNotifications(prev => [{ id, message, type }, ...prev].slice(0, 10));
    setTimeout(() => { dismissedIds.current.add(id); setNotifications(prev => prev.filter(n => n.id !== id)); }, 4000);
    if (soundEnabled) {
      const a = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBg==');
      a.volume = 0.2; a.play().catch(() => {});
    }
  };

  const updateTelegramChannel = (index, value) => {
    setConfig(prev => ({
      ...prev,
      telegram: {
        ...prev.telegram,
        channelNames: prev.telegram.channelNames.map((channel, i) => i === index ? value : channel),
        channelName: index === 0 ? value : (prev.telegram.channelNames[0] || '')
      }
    }));
  };

  const addTelegramChannel = () => {
    setConfig(prev => ({
      ...prev,
      telegram: {
        ...prev.telegram,
        channelNames: [...(prev.telegram.channelNames || ['']), '']
      }
    }));
  };

  const loadTelegramChats = async () => {
    setTelegramChatsLoading(true);
    try {
      const result = await apiService.getTelegramChats();
      setTelegramChats(result.chats || []);
    } catch (e) {
      console.error(e);
      addNotification('Failed to load Telegram chats', 'error');
    } finally {
      setTelegramChatsLoading(false);
    }
  };

  const selectTelegramChat = (index, value) => {
    updateTelegramChannel(index, value);
  };

  const loadAuditEntries = async (date = auditDate) => {
    setAuditLoading(true);
    try {
      const result = await apiService.getSignalAudit(date);
      setAuditEntries(result.entries || []);
    } catch (e) {
      console.error(e);
      addNotification('Failed to load audit log', 'error');
    } finally {
      setAuditLoading(false);
    }
  };

  const addChannelProfile = () => {
    setConfig(prev => ({
      ...prev,
      telegram: {
        ...prev.telegram,
        channelProfiles: [
          ...(prev.telegram.channelProfiles || []),
          {
            channel: '',
            label: '',
            enabled: true,
            lot_size: '',
            stop_loss_mode: '',
            risk_per_trade: '',
            enforce_entry_range: '',
            tp_orders: {},
          }
        ]
      }
    }));
  };

  const updateChannelProfile = (index, key, value) => {
    setConfig(prev => ({
      ...prev,
      telegram: {
        ...prev.telegram,
        channelProfiles: (prev.telegram.channelProfiles || []).map((profile, i) => (
          i === index ? { ...profile, [key]: value } : profile
        ))
      }
    }));
  };

  const removeChannelProfile = (index) => {
    setConfig(prev => ({
      ...prev,
      telegram: {
        ...prev.telegram,
        channelProfiles: (prev.telegram.channelProfiles || []).filter((_, i) => i !== index)
      }
    }));
  };

  const removeTelegramChannel = (index) => {
    setConfig(prev => {
      const nextChannels = (prev.telegram.channelNames || []).filter((_, i) => i !== index);
      const safeChannels = nextChannels.length ? nextChannels : [''];
      return {
        ...prev,
        telegram: {
          ...prev.telegram,
          channelNames: safeChannels,
          channelName: safeChannels[0]
        }
      };
    });
  };

  // Session timer
  useEffect(() => {
    if (isRunning && !sessionStart) setSessionStart(Date.now());
    if (!isRunning) { setSessionStart(null); setSessionElapsed(''); }
  }, [isRunning]);

  useEffect(() => {
    if (!sessionStart) return;
    const tick = () => {
      const diff = Date.now() - sessionStart;
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setSessionElapsed(h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sessionStart]);

  useEffect(() => {
    if (activeTab === 'audit') {
      loadAuditEntries(auditDate);
    }
  }, [activeTab, auditDate]);

  const handleToggle = async () => {
    setIsLoading(true);
    try {
      const result = isRunning ? await apiService.stop() : await apiService.start();
      if (result.success) { addNotification(isRunning ? 'System stopped' : 'System started', 'success'); setIsRunning(!isRunning); }
      else addNotification(result.message || 'Operation failed', 'error');
    } catch { addNotification('Connection error', 'error'); }
    finally { setIsLoading(false); }
  };

  const handleSaveConfig = async () => {
    setIsLoading(true);
    try {
      const channelNames = (config.telegram.channelNames || []).map(v => v.trim()).filter(Boolean);
      const result = await apiService.updateConfig({
        telegram: {
          api_id: config.telegram.apiId,
          api_hash: config.telegram.apiHash,
          channel_name: channelNames[0] || '',
          channel_names: channelNames,
          channel_profiles: (config.telegram.channelProfiles || [])
            .map(profile => ({
              channel: (profile.channel || '').trim(),
              label: (profile.label || '').trim(),
              enabled: profile.enabled !== false,
              lot_size: profile.lot_size === '' ? null : (parseFloat(profile.lot_size) || 0),
              stop_loss_mode: profile.stop_loss_mode || null,
              risk_per_trade: profile.risk_per_trade === '' ? null : (parseFloat(profile.risk_per_trade) || 0),
              enforce_entry_range: profile.enforce_entry_range === '' ? null : profile.enforce_entry_range,
              tp_orders: profile.tp_orders || {},
            }))
            .filter(profile => profile.channel)
        },
        mt5: { login: config.mt5.login, password: config.mt5.password, server: config.mt5.server },
        trading: {
          lot_size: parseFloat(config.trading.lotSize),
          auto_breakeven: config.trading.autoBreakeven,
          stop_loss_mode: config.trading.stopLossMode,
          risk_per_trade: parseFloat(config.trading.riskPerTrade) || 0,
          max_daily_loss: parseFloat(config.trading.maxDailyLoss) || 0,
          max_positions: parseInt(config.trading.maxPositions) || 0,
          trailing_stop: config.trading.trailingStop,
          max_tp_pips: config.trading.maxTpPips ? parseFloat(config.trading.maxTpPips) : null,
          tp_cap_increment: config.trading.tpCapIncrement ? parseFloat(config.trading.tpCapIncrement) : 50,
          tp_orders: {
            tp1: parseInt(config.trading.tpOrders.tp1) || 1,
            tp2: parseInt(config.trading.tpOrders.tp2) || 1,
            tp3: parseInt(config.trading.tpOrders.tp3) || 1,
          }
        }
      });
      addNotification(
        result.message || (result.success ? 'Configuration saved!' : 'Failed to save'),
        result.success ? 'success' : 'error'
      );
      if (result.success) {
        await loadConfig();
      }
    } catch { addNotification('Error saving config', 'error'); }
    finally { setIsLoading(false); }
  };

  const handleRefresh = async () => {
    try {
      const [s, st, sg, ac, hi] = await Promise.all([
        apiService.getStatus(), apiService.getStats(), apiService.getSignals(),
        apiService.getAccount(), apiService.getHistory()
      ]);
      if (s) { setIsRunning(s.running); setConnectionStatus({ telegram: s.telegram_connected, mt5: s.mt5_connected }); }
      if (st) setStats(st); if (sg) setSignals(sg); if (ac) setAccount(ac); if (hi) setTradeHistory(hi);
      addNotification('Refreshed!', 'success');
    } catch { addNotification('Refresh failed', 'error'); }
  };

  const loadHistory = async () => { try { const h = await apiService.getHistory(); if (h) setTradeHistory(h); } catch (e) { console.error(e); } };

  const loadDailyRecap = async () => {
    setRecapLoading(true);
    try {
      const r = await apiService.getDailyRecap();
      if (r) setDailyRecap(r);
    } catch (e) {
      console.error(e);
    } finally {
      setRecapLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'dashboard') return;
    loadDailyRecap();
    const id = setInterval(loadDailyRecap, 30000);
    return () => clearInterval(id);
  }, [activeTab]);

  const handleClosePosition = async (ticket) => {
    if (closingTickets.has(ticket)) return;
    if (!window.confirm(`Close position #${ticket}?`)) return;
    setClosingTickets(prev => new Set(prev).add(ticket));
    setPositions(prev => prev.filter(p => p.ticket !== ticket));
    try {
      const result = await apiService.closePosition(ticket);
      if (!result?.success) {
        const p = await apiService.getPositions().catch(() => null);
        if (p) setPositions(p);
      }
    } catch {
      const p = await apiService.getPositions().catch(() => null);
      if (p) setPositions(p);
    } finally {
      setClosingTickets(prev => { const n = new Set(prev); n.delete(ticket); return n; });
    }
  };

  const handleBreakeven = async (ticket) => {
    if (!window.confirm(`Move SL to entry for #${ticket}?`)) return;
    try {
      const result = await apiService.breakevenPosition(ticket);
      if (result?.success) {
        const p = await apiService.getPositions().catch(() => null);
        if (p) setPositions(p);
      } else {
        window.alert(result?.error || `Failed to BE #${ticket}`);
      }
    } catch (e) {
      window.alert(`Error: ${e.message || e}`);
    }
  };

  // Unified trades: open positions (live floating) + closed deals (realized).
  // Single source of truth for the Trades tab; replaces the old in-memory
  // signals list which double-counted per-TP/per-order entries.
  const unifiedTrades = (() => {
    const opens = (positions || []).map(p => ({
      ticket: p.ticket,
      time: p.time,
      symbol: p.symbol,
      side: p.type,
      volume: p.volume,
      price_open: p.price_open,
      price_current: p.price_current,
      sl: p.sl,
      tp: p.tp,
      status: 'OPEN',
      profit: Number(p.profit) || 0,
      net: Number(p.profit) || 0,
      provider: p.provider || '',
      original_message: p.original_message || '',
      _key: `o-${p.ticket}`,
    }));
    const closes = (tradeHistory || []).map(d => ({
      ticket: d.ticket,
      time: d.time,
      symbol: d.symbol,
      side: d.type,
      volume: d.volume,
      price_open: d.price,
      price_current: null,
      sl: null,
      tp: null,
      status: 'CLOSED',
      profit: Number(d.profit) || 0,
      net: (Number(d.profit) || 0) + (Number(d.commission) || 0) + (Number(d.swap) || 0),
      provider: d.provider || '',
      _key: `c-${d.ticket}`,
    }));
    return [...opens, ...closes];
  })();

  const filteredTrades = unifiedTrades
    .filter(tr => {
      if (filterStatus !== 'all' && tr.status !== filterStatus.toUpperCase()) return false;
      if (filterAction !== 'all' && tr.side !== filterAction) return false;
      if (filterPnL === 'win' && tr.profit <= 0) return false;
      if (filterPnL === 'loss' && tr.profit >= 0) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const haystack = `${tr.symbol || ''} ${tr.provider || ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'profit') return b.profit - a.profit;
      if (sortBy === 'pair') return (a.symbol || '').localeCompare(b.symbol || '');
      return new Date(b.time || 0) - new Date(a.time || 0);
    });

  const filteredPnLSum = filteredTrades.reduce((sum, tr) => sum + tr.profit, 0);
  const openCount = unifiedTrades.filter(t => t.status === 'OPEN').length;
  const closedCount = unifiedTrades.filter(t => t.status === 'CLOSED').length;
  const closedWins = unifiedTrades.filter(t => t.status === 'CLOSED' && t.profit > 0).length;
  const realizedSum = unifiedTrades.filter(t => t.status === 'CLOSED').reduce((s, t) => s + t.profit, 0);
  const floatingSum = unifiedTrades.filter(t => t.status === 'OPEN').reduce((s, t) => s + t.profit, 0);

  const exportTrades = async () => {
    try {
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      wb.creator = 'xceller.';
      wb.created = new Date();
      const ws = wb.addWorksheet('Trades', { views: [{ state: 'frozen', ySplit: 1 }] });

      ws.columns = [
        { header: 'Time', key: 'time', width: 22 },
        { header: 'Ticket', key: 'ticket', width: 14 },
        { header: 'Provider', key: 'provider', width: 22 },
        { header: 'Symbol', key: 'symbol', width: 12 },
        { header: 'Side', key: 'side', width: 8 },
        { header: 'Vol', key: 'volume', width: 8 },
        { header: 'Open', key: 'price_open', width: 12 },
        { header: 'SL', key: 'sl', width: 12 },
        { header: 'TP', key: 'tp', width: 12 },
        { header: 'Status', key: 'status', width: 10 },
        { header: 'P&L', key: 'profit', width: 12 },
        { header: 'Net', key: 'net', width: 12 },
      ];

      const headerRow = ws.getRow(1);
      headerRow.height = 28;
      headerRow.eachCell(cell => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6366F1' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = { bottom: { style: 'medium', color: { argb: 'FF4F46E5' } } };
      });

      filteredTrades.forEach(tr => {
        ws.addRow({
          time: tr.time ? new Date(tr.time).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '',
          ticket: tr.ticket || '',
          provider: tr.provider || '',
          symbol: tr.symbol || '',
          side: tr.side || '',
          volume: tr.volume || 0,
          price_open: tr.price_open || 0,
          sl: tr.sl || '',
          tp: tr.tp || '',
          status: tr.status,
          profit: tr.profit,
          net: tr.net,
        });
      });

      ws.eachRow((row, idx) => {
        if (idx === 1) return;
        const border = { top: { style: 'thin', color: { argb: 'FFE2E8F0' } }, bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } } };
        if (idx % 2 === 0) row.eachCell(cell => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } }; cell.border = border; });
        else row.eachCell(cell => { cell.border = border; });

        const sideCell = row.getCell('side');
        sideCell.font = { bold: true, color: { argb: sideCell.value === 'BUY' ? 'FF059669' : 'FFE11D48' } };
        ['profit', 'net'].forEach(key => {
          const c = row.getCell(key);
          c.numFmt = '+0.00;-0.00;0.00';
          c.font = { bold: true, color: { argb: (c.value || 0) >= 0 ? 'FF059669' : 'FFE11D48' } };
          c.alignment = { horizontal: 'right' };
        });
      });

      ws.autoFilter = { from: 'A1', to: `L${ws.rowCount}` };

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `xceller-trades-${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { console.error('Export failed:', err); }
  };
  const auditSummary = auditEntries.reduce((summary, entry) => {
    const kind = entry.kind || 'unknown';
    const status = entry.status || 'unknown';
    summary[kind] = (summary[kind] || 0) + 1;
    summary[`status:${status}`] = (summary[`status:${status}`] || 0) + 1;
    return summary;
  }, {});

  const themeTransRef = React.useRef(false);
  const toggleTheme = () => {
    if (themeTransRef.current) return;
    themeTransRef.current = true;
    setThemeTransition(true);
    setTimeout(() => setTheme(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('xceller_theme', next);
      return next;
    }), 300);
    setTimeout(() => { setThemeTransition(false); themeTransRef.current = false; }, 900);
  };

  const totalRealizedPnL = tradeHistory.reduce((s, d) => s + d.profit, 0);
  const liveProfit = account.profit || 0;
  const isConnectedToMT5 = connectionStatus.mt5;
  const enabledProviderCount = ((config.telegram.channelProfiles || []).filter(profile => profile.enabled !== false).length)
    || (config.telegram.channelNames || []).filter(Boolean).length;
  const latestProvider = signals[0]?.provider || signals[0]?.source || 'Awaiting live signal flow';

  /* ── Theme tokens ── */
  const iriSoft = getIriSoft(isDark);

  const t = getTokens(isDark);

  const badge = (type) => {
    if (type === 'BUY') return { background: t.badgeBuyBg, color: t.badgeBuyText, border: `1px solid ${t.badgeBuyBorder}` };
    return { background: t.badgeSellBg, color: t.badgeSellText, border: `1px solid ${t.badgeSellBorder}` };
  };
  const statusBadge = (status) => {
    if (status === 'open') return { background: t.badgeOpenBg, color: t.badgeOpenText, border: `1px solid ${t.badgeOpenBorder}` };
    if (status === 'closed') return { background: t.badgeClosedBg, color: t.badgeClosedText, border: `1px solid ${t.badgeClosedBorder}` };
    return { background: t.badgeExecBg, color: t.badgeExecText, border: `1px solid ${t.badgeExecBorder}` };
  };

  const glassCard = {
    background: [
      isDark
        ? 'linear-gradient(145deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.055) 26%, rgba(125,249,198,0.03) 58%, rgba(143,183,255,0.06) 100%)'
        : 'linear-gradient(145deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.70) 30%, rgba(143,183,255,0.14) 68%, rgba(125,249,198,0.10) 100%)',
      isDark
        ? 'radial-gradient(circle at top left, rgba(255,255,255,0.12), transparent 40%)'
        : 'radial-gradient(circle at top left, rgba(255,255,255,0.95), transparent 42%)',
      isDark
        ? 'linear-gradient(118deg, transparent 54%, rgba(111,168,255,0.16) 70%, rgba(125,249,198,0.10) 82%, rgba(242,201,120,0.12) 92%, transparent 100%)'
        : 'linear-gradient(118deg, transparent 48%, rgba(111,168,255,0.16) 66%, rgba(125,249,198,0.12) 80%, rgba(242,201,120,0.12) 92%, transparent 100%)',
      t.cardBg,
    ].join(', '),
    backdropFilter: 'blur(34px) saturate(1.35)',
    WebkitBackdropFilter: 'blur(34px) saturate(1.35)',
    border: `1px solid ${t.cardBorder}`,
    borderRadius: 24,
    transition: 'all 0.3s ease',
    boxShadow: `${t.iriShadow}, inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.72)'}, inset 0 -24px 44px ${isDark ? 'rgba(255,255,255,0.018)' : 'rgba(255,255,255,0.18)'}`,
    position: 'relative',
    overflow: 'hidden',
    isolation: 'isolate',
  };
  const glassRow = {
    background: [
      isDark
        ? 'linear-gradient(180deg, rgba(255,255,255,0.065), rgba(255,255,255,0.022))'
        : 'linear-gradient(180deg, rgba(255,255,255,0.84), rgba(255,255,255,0.54))',
      isDark
        ? 'linear-gradient(118deg, transparent 58%, rgba(111,168,255,0.11) 74%, rgba(125,249,198,0.07) 90%, transparent 100%)'
        : 'linear-gradient(118deg, transparent 52%, rgba(111,168,255,0.10) 72%, rgba(125,249,198,0.08) 90%, transparent 100%)',
      t.rowBg,
    ].join(', '),
    backdropFilter: 'blur(24px) saturate(1.25)',
    WebkitBackdropFilter: 'blur(24px) saturate(1.25)',
    border: `1px solid ${t.rowBorder}`,
    borderRadius: 16,
    transition: 'all 0.2s ease',
    boxShadow: `inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.64)'}`,
    position: 'relative',
    overflow: 'hidden',
    isolation: 'isolate',
  };
  const glassInput = {
    background: [
      isDark
        ? 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))'
        : 'linear-gradient(180deg, rgba(255,255,255,0.88), rgba(255,255,255,0.56))',
      isDark
        ? 'linear-gradient(118deg, transparent 55%, rgba(111,168,255,0.12) 74%, rgba(125,249,198,0.08) 90%, transparent 100%)'
        : 'linear-gradient(118deg, transparent 48%, rgba(111,168,255,0.10) 70%, rgba(125,249,198,0.08) 88%, transparent 100%)',
      t.inputBg,
    ].join(', '),
    backdropFilter: 'blur(22px)',
    border: `1px solid ${t.inputBorder}`,
    borderRadius: 16,
    color: t.text,
    transition: 'all 0.2s',
    width: '100%',
    padding: '12px 14px',
    fontSize: 13,
    outline: 'none',
    boxShadow: `inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.64)'}`,
  };
  const btnIcon = { background: t.btnIconBg, border: `1px solid ${t.btnIconBorder}`, borderRadius: 14, color: t.textMuted, cursor: 'pointer', padding: 8, transition: 'all 0.25s', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)'}` };
  const badgeStyle = { borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', display: 'inline-block' };

  const colHeader = { fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.textFaint };
  const monoMuted = { fontSize: 12, fontFamily: monoFont, color: t.textMuted };
  const gridCols = '90px 100px 60px 90px 90px 1fr 70px 90px';
  const flatRow = {
    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.72)',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
    borderRadius: 12,
  };
  const monoSolid = { fontSize: 12, fontFamily: monoFont, color: isDark ? 'rgba(255,255,255,0.82)' : 'rgba(0,0,0,0.78)' };

  // Currency symbol from the live MT5 account. Defaults to € if no account
  // info has arrived yet so initial render isn't dollar-flavoured.
  const ccySymbol = (() => {
    const c = String(account?.currency || 'EUR').toUpperCase();
    if (c === 'USD') return '$';
    if (c === 'GBP') return '£';
    if (c === 'JPY') return '¥';
    return '€';
  })();
  const fmtMoney = (v, { signed = true, decimals = 2 } = {}) => {
    const num = Number(v) || 0;
    const prefix = signed && num > 0 ? '+' : num < 0 ? '-' : '';
    return `${prefix}${ccySymbol}${Math.abs(num).toFixed(decimals)}`;
  };

  const parseTpPrice = (value) => {
    const raw = String(Array.isArray(value) ? value[value.length - 1] : value || '');
    const parsed = parseFloat(raw.replace(/[^\d.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  };
  // Backend is authoritative: only show a cap badge when the trade result says
  // the broker TP was actually capped.
  const getEffectiveTp = (sig) => {
    if (sig?.tp_capped) {
      const price = parseTpPrice(sig.tp);
      const original = sig.signal_tp || '';
      const tpLabelMatch = String(sig.tp || original).match(/TP\s*(\d+)/i);
      const tpNumber = tpLabelMatch ? parseInt(tpLabelMatch[1], 10) : null;
      return price == null ? null : { price, original, tpNumber };
    }

    return null;
  };

  return (
    <div style={{ minHeight: '100vh', background: t.bg, fontFamily: bodyFont, color: t.text, position: 'relative', overflow: 'hidden', transition: 'background 0.6s ease, color 0.4s ease' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&family=Space+Grotesk:wght@400;500;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        @keyframes slideDown { from { opacity:0; transform:translateY(-12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes tabEnter { from { opacity:0; transform:translateY(16px) scale(0.99); } to { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes cardPop { from { opacity:0; transform:translateY(20px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes heroFloatA { 0%,100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(-18px, 12px, 0) scale(1.04); } }
        @keyframes heroFloatB { 0%,100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(18px, -14px, 0) scale(0.96); } }
        @keyframes heroSweep { 0% { transform: translateX(-120%) skewX(-16deg); opacity: 0; } 15% { opacity: 0.55; } 70% { opacity: 0.48; } 100% { transform: translateX(220%) skewX(-16deg); opacity: 0; } }
        .tab-panel { animation: tabEnter 0.35s cubic-bezier(0.16,1,0.3,1) both; }
        .card-enter { animation: cardPop 0.4s cubic-bezier(0.16,1,0.3,1) both; }
        @keyframes spin { to { transform:rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
        @keyframes shimmer { 0% { transform:translateX(-100%); } 100% { transform:translateX(100%); } }
        .skeleton-shimmer::after {
          content:''; position:absolute; inset:0;
          background: linear-gradient(90deg, transparent 0%, ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.3)'} 50%, transparent 100%);
          animation: shimmer 1.5s ease infinite;
        }
        @keyframes iriShift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        .iri-border { position: relative; }
        .iri-border::before {
          content: ''; position: absolute; inset: -1px; border-radius: inherit; padding: 1px;
          background: ${iriSoft}; background-size: 300% 300%; animation: iriShift 6s ease infinite;
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask-composite: xor; mask-composite: exclude;
          pointer-events: none; opacity: ${isDark ? 0.66 : 0.78};
        }
        .iri-text {
          background: ${iri}; background-size: 300% 300%; animation: iriShift 4s ease infinite;
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .iri-line {
          background: ${iri}; background-size: 300% 300%; animation: iriShift 4s ease infinite;
        }
        .dashboard-hero-orb {
          position: absolute;
          border-radius: 999px;
          pointer-events: none;
          mix-blend-mode: screen;
          filter: blur(20px);
        }
        .dashboard-hero-sheen {
          position: absolute;
          inset: 0;
          pointer-events: none;
          overflow: hidden;
        }
        .dashboard-hero-sheen::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 38%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(111,168,255,0.18) 40%, rgba(111,168,255,0.28) 50%, rgba(125,249,198,0.18) 62%, transparent);
          animation: heroSweep 5.5s ease-in-out infinite;
        }
        @keyframes themeWipe {
          0%   { opacity: 0; transform: scale(0.3); filter: blur(40px); }
          40%  { opacity: 1; transform: scale(1.1); filter: blur(0px); }
          70%  { opacity: 0.8; transform: scale(1.15); filter: blur(0px); }
          100% { opacity: 0; transform: scale(1.6); filter: blur(20px); }
        }
        .theme-wipe {
          animation: themeWipe 0.9s cubic-bezier(0.16,1,0.3,1) both;
        }
        @keyframes themeFlash {
          0%   { opacity: 0; }
          30%  { opacity: 0.15; }
          100% { opacity: 0; }
        }
        .theme-flash {
          animation: themeFlash 0.9s ease both;
        }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: ${isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'}; border-radius: 10px; }
        ::-webkit-scrollbar-thumb { background: ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)'}; border-radius: 10px; border: 1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}; }
        ::-webkit-scrollbar-thumb:hover { background: ${isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)'}; }
        ::-webkit-scrollbar-corner { background: transparent; }
        ::placeholder { color: ${t.textFaint}; }
        @media (max-width: 768px) {
          .dash-header { padding: 0 12px !important; }
          .dash-logo-sub { display: none !important; }
          .dash-conn-labels { display: none !important; }
          .dash-header-divider { display: none !important; }
          .dash-status-badge { display: none !important; }
          .dash-sound-btn { display: none !important; }
          .dash-header-right { gap: 4px !important; }
          .dash-tab-nav { overflow-x: auto !important; -webkit-overflow-scrolling: touch; scrollbar-width: none !important; padding: 0 10px !important; }
          .dash-tab-nav::-webkit-scrollbar { display: none !important; }
          .dash-tab-btn { padding: 12px 10px !important; font-size: 11px !important; white-space: nowrap !important; gap: 5px !important; }
          .dash-tab-label { display: none !important; }
          .dash-main { padding: 14px 10px !important; }
          .dash-hero { padding: 18px !important; }
          .dash-notifications { left: 8px !important; right: 8px !important; max-width: calc(100vw - 16px) !important; }
          .dash-account-grid { grid-template-columns: 1fr 1fr 1fr !important; }
          .dash-account-stat { padding: 4px 6px !important; }
          .dash-account-divider { display: none !important; }
          .signals-table-outer { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .signals-table-header-row, .signals-table-scroll { min-width: 570px; }
          .hist-table-outer { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .hist-table-header-row, .hist-table-scroll { min-width: 720px; }
          .dash-filter-bar { flex-wrap: wrap !important; gap: 6px !important; }
          .dash-filter-bar > div { min-width: 130px; flex: 1 1 130px; }
          .history-stats-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* Ambient background */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: t.ambientGradient, transition: 'background 0.8s ease' }} />

      {/* Theme transition overlay */}
      {themeTransition && (
        <>
          <div className="theme-wipe" style={{ position: 'fixed', inset: '-20%', zIndex: 200, pointerEvents: 'none', borderRadius: '50%',
            background: `conic-gradient(from 0deg, rgba(248,251,255,0.45), rgba(143,183,255,0.45), rgba(125,249,198,0.40), rgba(242,201,120,0.40), rgba(106,163,255,0.40), rgba(248,251,255,0.45))`,
          }} />
          <div className="theme-flash" style={{ position: 'fixed', inset: 0, zIndex: 199, pointerEvents: 'none',
            background: isDark ? 'white' : '#040506',
          }} />
        </>
      )}

      {/* ── Header ── */}
      <header className="dash-header" style={{ position: 'sticky', top: 0, zIndex: 50, padding: '0 24px', background: t.headerBg, backdropFilter: 'blur(56px) saturate(1.5)', WebkitBackdropFilter: 'blur(56px) saturate(1.5)', borderBottom: `1px solid ${t.headerBorder}`, boxShadow: `inset 0 -1px 0 ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.3)'}, ${t.iriHeaderShadow}` }}>
        <div className="dash-header-inner" style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'inherit' }}>
            <div className="iri-border" style={{ width: 40, height: 40, ...glassCard, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
              <FallingStarLogo size={30} />
            </div>
            <div>
              <div className="iri-text brand-display" style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}>xceller.</div>
              <div className="dash-logo-sub" style={{ fontSize: 10, color: t.textFaint, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600 }}>Signal Automation</div>
            </div>
          </Link>

          <div className="dash-conn-labels" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: t.textDim, fontWeight: 600 }}>
              <div style={{ width: 7, height: 7, background: connectionStatus.mt5 ? t.connDotOn : t.connDotOff, borderRadius: '50%' }} />MT5
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: t.textDim, fontWeight: 600 }}>
              <div style={{ width: 7, height: 7, background: connectionStatus.telegram ? t.connDotOn : t.connDotOff, borderRadius: '50%' }} />TG
            </div>
            {goldPrice && (
              <div title={`XAUUSD  bid ${goldPrice.bid?.toFixed(2)} / ask ${goldPrice.ask?.toFixed(2)}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: t.sectionBg, border: `1px solid ${t.sectionBorder}`, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' }}>
                <span style={{ color: '#f2c978' }}>XAU</span>
                <span style={{ fontFamily: monoFont, color: t.text }}>${goldPrice.bid?.toFixed(2)}</span>
                <span style={{ color: t.textFaint, fontSize: 10 }}>sp {goldPrice.spread?.toFixed(1)}</span>
              </div>
            )}
          </div>

          <div className="dash-header-right" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button style={btnIcon} onClick={toggleTheme} title="Toggle theme">
              {isDark ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <button style={btnIcon} onClick={() => setNotificationsEnabled(p => !p)}>
              {notificationsEnabled ? <Bell size={15} /> : <BellOff size={15} />}
            </button>
            <button className="dash-sound-btn" style={btnIcon} onClick={() => setSoundEnabled(p => !p)}>
              {soundEnabled ? <Volume2 size={15} /> : <VolumeX size={15} />}
            </button>
            <button style={btnIcon} onClick={handleRefresh}>
              <RefreshCw size={15} className={isLoading ? 'spin' : ''} />
            </button>
            <Link to="/fitness" style={{ ...btnIcon, textDecoration: 'none' }} title="Open Fitness">
              <Dumbbell size={15} />
            </Link>

            <div className="dash-header-divider" style={{ width: 1, height: 28, background: t.divider }} />

            {isRunning
              ? <div className="dash-status-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: t.statusLiveBg, border: `1px solid ${t.statusLiveBorder}`, borderRadius: 20, padding: '4px 12px', color: t.statusLiveText, fontSize: 12, fontWeight: 700, letterSpacing: '0.08em' }}>
                  <div style={{ width: 6, height: 6, background: t.connDotOn, borderRadius: '50%' }} />ACTIVE
                </div>
              : <div className="dash-status-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: t.statusStandbyBg, border: `1px solid ${t.statusStandbyBorder}`, borderRadius: 20, padding: '4px 12px', color: t.statusStandbyText, fontSize: 12, fontWeight: 700, letterSpacing: '0.08em' }}>
                  <div style={{ width: 6, height: 6, background: t.connDotOff, borderRadius: '50%' }} />STANDBY
                </div>}

            {isAdmin && (
              <button onClick={handleToggle} disabled={isLoading}
                style={{ background: isRunning ? t.stopBg : t.startBg, border: `1px solid ${isRunning ? t.stopBorder : t.startBorder}`, borderRadius: 12, color: isRunning ? t.stopText : t.startText, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', letterSpacing: '0.05em', padding: '8px 20px', fontSize: 13, opacity: isLoading ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                {isLoading ? <RefreshCw size={13} className="spin" /> : isRunning ? <Pause size={13} /> : <Play size={13} />}
                {isLoading ? 'WAIT' : isRunning ? 'STOP' : 'START'}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ── Navigation ── */}
      <nav style={{ background: t.navBg, borderBottom: `1px solid ${t.navBorder}`, backdropFilter: 'blur(48px) saturate(1.4)', WebkitBackdropFilter: 'blur(48px) saturate(1.4)', position: 'sticky', top: 64, zIndex: 40, transition: 'background 0.6s ease, border-color 0.6s ease' }}>
        <div className="dash-tab-nav" style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', display: 'flex', gap: 4 }}>
          {[
            { id: 'dashboard', icon: Activity, label: 'Dashboard' },
            { id: 'history', icon: History, label: 'Trades', fn: loadHistory },
            { id: 'analytics', icon: BarChart3, label: 'Performance', fn: loadHistory },
            { id: 'journal', icon: BookOpen, label: 'Journal' },
            { id: 'audit', icon: Search, label: 'Audit', fn: () => loadAuditEntries(auditDate) },
            ...(isAdmin ? [{ id: 'config', icon: Settings, label: 'Config' }] : []),
          ].map(tab => (
            <button key={tab.id} className="dash-tab-btn" onClick={() => { setActiveTab(tab.id); tab.fn && tab.fn(); }}
              style={{ position: 'relative', padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, color: activeTab === tab.id ? t.text : t.textDim, transition: 'color 0.2s' }}>
              <tab.icon size={14} />
              <span className="dash-tab-label">{tab.label}</span>
              {activeTab === tab.id && <div className="iri-line" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, borderRadius: 2, opacity: 0.7 }} />}
            </button>
          ))}
        </div>
      </nav>

      {/* ── Main ── */}
      <main className="dash-main" style={{ maxWidth: 1280, margin: '0 auto', padding: '28px 24px', position: 'relative', zIndex: 10 }}>

        {/* ═══ DASHBOARD ═══ */}
        {activeTab === 'dashboard' && !initialLoaded && (
          <div className="tab-panel" key="dashboard-skeleton" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <SkeletonCard lines={2} t={t} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }} className="grid-mobile-2">
              {[0,1,2,3].map(i => <SkeletonCard key={i} lines={1} t={t} />)}
            </div>
            <SkeletonCard lines={4} t={t} />
          </div>
        )}
        {activeTab === 'dashboard' && initialLoaded && (
          <div className="tab-panel" key="dashboard" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Account */}
            <div className={`card-enter prism-panel ${hovered === 'account' ? 'iri-border' : ''}`}
              onMouseEnter={() => setHovered('account')} onMouseLeave={() => setHovered(null)}
              style={{ ...glassCard, padding: 24, animationDelay: '0.04s',
                ...(hovered === 'account' ? { background: t.cardHoverBg, borderColor: t.cardHoverBorder } : {}) }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 32, height: 32, background: t.iconBg, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${t.iconBorder}` }}>
                    <Wallet size={15} color={t.textMuted} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.textFaint }}>Live Account</div>
                    <div style={{ fontSize: 12, color: t.textDim, marginTop: 1 }}>{account.server || 'Not connected'} {account.currency ? `· ${account.currency}` : ''}</div>
                  </div>
                </div>
                {isConnectedToMT5 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: t.positive, letterSpacing: '0.08em' }}>
                    <Zap size={11} fill={t.positive} color={t.positive} />LIVE{sessionElapsed ? ` · ${sessionElapsed}` : ''}
                  </div>
                )}
              </div>
              <div className="dash-account-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr 1px 1fr' }}>
                <div className="dash-account-stat" style={{ padding: '4px 20px', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: t.textFaint, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Balance</div>
                  <AnimatedNumber value={account.balance || 0} prefix="$" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1, color: t.text }} />
                </div>
                <div className="dash-account-divider" style={{ background: t.divider }} />
                <div className="dash-account-stat" style={{ padding: '4px 20px', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: t.textFaint, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Equity</div>
                  <AnimatedNumber value={account.equity || 0} prefix="$" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1, color: t.text }} />
                </div>
                <div className="dash-account-divider" style={{ background: t.divider }} />
                <div className="dash-account-stat" style={{ padding: '4px 20px', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: t.textFaint, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Floating P&L</div>
                  <AnimatedNumber value={Math.abs(liveProfit)} prefix={`${liveProfit >= 0 ? '+' : '-'}${ccySymbol}`} style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1, color: liveProfit >= 0 ? t.positive : t.negative }} />
                </div>
              </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }} className="grid-mobile-2">
              {[
                { label: 'Total Signals', numVal: stats.total_signals || 0, icon: Signal, id: 'stat0', decimals: 0 },
                (() => {
                  // Same value as the Daily Recap card's Net P&L chip, so they
                  // never disagree. Backend computes realized + floating per day.
                  const net = Number(dailyRecap?.totals?.net) || 0;
                  return { label: 'Daily Profit', numVal: net, icon: DollarSign, pnl: true, pos: net >= 0, id: 'stat1', prefix: `${net >= 0 ? '+' : '-'}${ccySymbol}` };
                })(),
                { label: 'Win Rate', numVal: stats.win_rate || 0, icon: Activity, id: 'stat2', suffix: '%', decimals: 1 },
                { label: 'Session P&L', numVal: stats.profit || 0, icon: Wallet, pnl: true, pos: stats.profit >= 0, id: 'stat3', prefix: `${stats.profit >= 0 ? '+' : '-'}${ccySymbol}` },
              ].map((s, i) => (
                <div key={i} className={`card-enter prism-panel ${hovered === s.id ? 'iri-border' : ''}`}
                  onMouseEnter={() => setHovered(s.id)} onMouseLeave={() => setHovered(null)}
                  style={{ ...glassCard, padding: '18px 20px', animationDelay: `${0.08 + i * 0.06}s`, cursor: 'default',
                    ...(hovered === s.id ? { background: t.cardHoverBg, borderColor: t.cardHoverBorder, transform: 'translateY(-2px)' } : {}) }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: t.textFaint, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{s.label}</span>
                    <div style={{ width: 28, height: 28, background: t.iconBg, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${t.iconBorder}`, transition: 'all 0.25s' }}>
                      <s.icon size={13} color={t.textMuted} />
                    </div>
                  </div>
                  <AnimatedNumber value={Math.abs(s.numVal)} prefix={s.prefix || ''} suffix={s.suffix || ''} decimals={s.decimals ?? 2}
                    style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', color: s.pnl ? (s.pos ? t.positive : t.negative) : t.text }} />
                </div>
              ))}
            </div>

            {/* Gold chart — collapsible. Iframe only mounts when expanded so
                we don't pay the TradingView load cost on every dashboard visit. */}
            <div className="card-enter prism-panel" style={{ ...glassCard, padding: chartExpanded ? 18 : '12px 18px', animationDelay: '0.24s' }}>
              <button onClick={() => setChartExpanded(v => !v)} style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                color: 'inherit', textAlign: 'left',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <TrendingUp size={15} color={t.textMuted} />
                  <span style={{ fontWeight: 700, fontSize: 14 }}>XAUUSD · Live Chart</span>
                  <span style={{ fontSize: 10, color: t.textFaint, letterSpacing: '0.1em', textTransform: 'uppercase' }}>OANDA</span>
                  {!chartExpanded && (
                    <span style={{ fontSize: 10, color: t.textFaint, fontStyle: 'italic' }}>
                      · click to expand
                    </span>
                  )}
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 28, height: 28, borderRadius: 8,
                  background: t.sectionBg, border: `1px solid ${t.sectionBorder}`,
                  color: t.textMuted, transition: 'transform 0.25s ease',
                  transform: chartExpanded ? 'rotate(180deg)' : 'rotate(0)',
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </button>
              {chartExpanded && (
                <div style={{ marginTop: 12, animation: 'slideDown 0.3s ease' }}>
                  <TradingViewChart symbol="OANDA:XAUUSD" interval="15" height={320} t={t} isDark={isDark} />
                </div>
              )}
            </div>

            {/* Equity Curve + Live Feed side by side */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }} className="grid-mobile-1">
              <div className="card-enter" style={{ animationDelay: '0.28s' }}>
                <EquityCurve tradeHistory={tradeHistory} account={account} t={t} isDark={isDark} glassCard={glassCard} />
              </div>
              {/* Open Positions (compact) */}
              <div className="card-enter prism-panel" style={{ ...glassCard, padding: 20, animationDelay: '0.32s', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: positions.length > 0 ? t.positive : t.textFaint, boxShadow: positions.length > 0 ? `0 0 8px ${t.positive}` : 'none', transition: 'all 0.3s' }} />
                    <span style={{ fontWeight: 700, fontSize: 14 }}>Open Positions</span>
                    <span style={{ fontSize: 11, color: t.textFaint, background: t.countBg, border: `1px solid ${t.countBorder}`, borderRadius: 6, padding: '1px 7px' }}>{positions.length}</span>
                  </div>
                  {positions.length > 0 && (() => {
                    const totalPnL = positions.reduce((s, p) => s + (p.profit || 0), 0);
                    return (
                      <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 700, color: totalPnL >= 0 ? t.positive : t.negative }}>
                        {fmtMoney(totalPnL)}
                      </span>
                    );
                  })()}
                </div>
                <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220 }}>
                  {positions.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: t.textFaint, fontSize: 12 }}>
                      {isConnectedToMT5 ? 'No open positions' : 'MT5 offline'}
                    </div>
                  ) : (
                    positions.map((pos, i) => {
                      const pnl = pos.profit || 0;
                      const isClosing = closingTickets.has(pos.ticket);
                      return (
                        <div key={pos.ticket} style={{
                          display: 'grid', gridTemplateColumns: '46px 1fr auto auto',
                          alignItems: 'center', gap: 8,
                          padding: '8px 10px', background: t.sectionBg, border: `1px solid ${t.sectionBorder}`,
                          borderRadius: 10, fontSize: 12, animation: `slideUp 0.2s ease ${Math.min(i,10) * 0.05}s both`,
                          opacity: isClosing ? 0.45 : 1,
                        }}>
                          <span style={{
                            ...badgeStyle, fontSize: 9, padding: '1px 6px', textAlign: 'center',
                            ...(pos.type === 'BUY' ? { background: t.badgeBuyBg, color: t.badgeBuyText, border: `1px solid ${t.badgeBuyBorder}` } : { background: t.badgeSellBg, color: t.badgeSellText, border: `1px solid ${t.badgeSellBorder}` }),
                          }}>{pos.type}</span>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                            <span style={{ fontWeight: 700, fontSize: 12, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {pos.symbol} <span style={{ fontFamily: monoFont, fontWeight: 500, color: t.textFaint, fontSize: 10 }}>{pos.volume}</span>
                            </span>
                            <span style={{ fontSize: 10, color: t.textFaint, fontFamily: monoFont }}>
                              #{pos.ticket} · @{pos.price_open?.toFixed(2)}
                            </span>
                          </div>
                          <span style={{ fontFamily: monoFont, fontWeight: 700, fontSize: 12, color: pnl >= 0 ? t.positive : t.negative, textAlign: 'right' }}>
                            {fmtMoney(pnl)}
                          </span>
                          {isAdmin && (
                            <button
                              onClick={() => handleClosePosition(pos.ticket)}
                              disabled={isClosing}
                              title={`Close #${pos.ticket}`}
                              style={{
                                background: t.stopBg, border: `1px solid ${t.stopBorder}`,
                                color: t.stopText, borderRadius: 6, padding: '3px 7px',
                                fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
                                cursor: isClosing ? 'not-allowed' : 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: 3,
                              }}>
                              <X size={9} />CLOSE
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Recent Trades — real MT5 data (closed deals + open positions),
                sorted by time desc. Click anywhere → opens the Trades tab. */}
            {(() => {
              const recent = unifiedTrades
                .slice()
                .sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0))
                .slice(0, 8);
              return (
                <div className="card-enter prism-panel" style={{ ...glassCard, padding: 22, animationDelay: '0.36s' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Activity size={15} color={t.textMuted} />
                      <span style={{ fontWeight: 700, fontSize: 14 }}>Recent Trades</span>
                      <span style={{ fontSize: 11, color: t.textFaint, background: t.countBg, border: `1px solid ${t.countBorder}`, borderRadius: 6, padding: '1px 7px' }}>{recent.length}</span>
                    </div>
                    <button onClick={() => setActiveTab('history')} style={{
                      background: 'transparent', border: 'none', color: t.textMuted,
                      fontSize: 11, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.04em',
                    }}>View all →</button>
                  </div>
                  {recent.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: t.textFaint, fontSize: 12 }}>
                      No trades yet
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {recent.map(tr => {
                        const isOpen = tr.status === 'OPEN';
                        return (
                          <div key={tr._key}
                            title={tr.original_message ? `Original signal:\n\n${tr.original_message}` : undefined}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '90px 100px 50px minmax(0, 1fr) 70px 90px',
                              gap: 10, alignItems: 'center',
                              padding: '9px 14px', background: t.sectionBg,
                              border: `1px solid ${t.sectionBorder}`, borderRadius: 10,
                              cursor: tr.original_message ? 'help' : 'default',
                            }}>
                            <span style={{ fontSize: 11, fontFamily: monoFont, color: t.textMuted }}>
                              {tr.time ? new Date(tr.time).toLocaleString('en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {tr.symbol}
                            </span>
                            <span style={{ ...badgeStyle, ...badge(tr.side), fontSize: 9 }}>{tr.side}</span>
                            <span title={tr.provider} style={{ fontSize: 11, color: tr.provider ? t.textMuted : t.textFaint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {tr.provider || '—'}
                            </span>
                            <span style={{
                              fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', padding: '2px 7px', borderRadius: 999,
                              background: isOpen ? 'rgba(74,222,128,0.14)' : 'rgba(148,163,184,0.14)',
                              border: `1px solid ${isOpen ? 'rgba(74,222,128,0.32)' : 'rgba(148,163,184,0.28)'}`,
                              color: isOpen ? '#4ade80' : t.textMuted, textAlign: 'center', justifySelf: 'center',
                            }}>{tr.status}</span>
                            <span style={{
                              fontFamily: monoFont, fontSize: 12, fontWeight: 700, textAlign: 'right',
                              color: tr.profit >= 0 ? t.positive : t.negative,
                            }}>
                              {fmtMoney(tr.profit)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Daily Recap */}
            <div className={`card-enter prism-panel ${hovered === 'recap' ? 'iri-border' : ''}`}
              onMouseEnter={() => setHovered('recap')} onMouseLeave={() => setHovered(null)}
              style={{ ...glassCard, padding: 24, animationDelay: '0.35s',
                ...(hovered === 'recap' ? { background: t.cardHoverBg, borderColor: t.cardHoverBorder } : {}) }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <BookOpen size={15} color={t.textMuted} />
                  <span style={{ fontWeight: 700, fontSize: 15 }}>Daily Recap</span>
                  <span style={{ fontSize: 11, color: t.textFaint }}>{dailyRecap?.date || today}</span>
                </div>
                <button style={btnIcon} onClick={loadDailyRecap} title="Refresh recap">
                  <RefreshCw size={14} className={recapLoading ? 'spin' : ''} />
                </button>
              </div>
              {!dailyRecap || (dailyRecap.providers || []).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: t.textFaint, fontSize: 12 }}>
                  {recapLoading ? 'Loading…' : 'No activity today'}
                </div>
              ) : (() => {
                const tot = dailyRecap.totals || {};
                const totalNet = tot.net || 0;
                const winRate = (tot.wins + tot.losses) ? (tot.wins / (tot.wins + tot.losses) * 100) : 0;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {/* Top totals chip strip */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }} className="grid-mobile-2">
                      {[
                        { label: 'Net P&L', value: fmtMoney(totalNet), tone: totalNet >= 0 ? t.positive : t.negative },
                        { label: 'Realized', value: fmtMoney(tot.realized_profit || 0), tone: tot.realized_profit >= 0 ? t.positive : t.negative },
                        { label: 'Floating', value: fmtMoney(tot.open_floating || 0), tone: tot.open_floating >= 0 ? t.positive : t.negative },
                        { label: 'Trades', value: `${tot.trades_opened || 0}`, sub: `${tot.closed_count || 0} closed · ${tot.open_count || 0} open` },
                        { label: 'Win Rate', value: `${winRate.toFixed(1)}%`, sub: `${tot.wins || 0}W / ${tot.losses || 0}L` },
                      ].map((x) => (
                        <div key={x.label} style={{ padding: '12px 14px', borderRadius: 14, background: t.sectionBg, border: `1px solid ${t.sectionBorder}` }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.textFaint, marginBottom: 6 }}>{x.label}</div>
                          <div style={{ fontSize: 18, fontWeight: 800, fontFamily: monoFont, color: x.tone || t.text }}>{x.value}</div>
                          {x.sub && <div style={{ fontSize: 10, color: t.textFaint, marginTop: 3 }}>{x.sub}</div>}
                        </div>
                      ))}
                    </div>
                    {/* Per-provider table */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 4 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 60px 60px 60px 90px 90px 90px 80px', gap: 10, padding: '0 14px 4px', ...colHeader }}>
                        <span>Provider</span>
                        <span style={{ textAlign: 'right' }}>Trades</span>
                        <span style={{ textAlign: 'right' }}>Open</span>
                        <span style={{ textAlign: 'right' }}>W/L</span>
                        <span style={{ textAlign: 'right' }}>Realized</span>
                        <span style={{ textAlign: 'right' }}>Floating</span>
                        <span style={{ textAlign: 'right' }}>Net</span>
                        <span style={{ textAlign: 'right' }}>Mgmt</span>
                      </div>
                      {(dailyRecap.providers || []).map((p) => {
                        const realized = p.realized_profit || 0;
                        const floating = p.open_floating || 0;
                        const net = p.net || 0;
                        const mgmtFail = p.mgmt_failed || 0;
                        const mgmtTot = p.mgmt_total || 0;
                        return (
                          <div key={p.provider} style={{ ...glassRow, padding: '11px 14px', display: 'grid', gridTemplateColumns: '1.7fr 60px 60px 60px 90px 90px 90px 80px', gap: 10, alignItems: 'center' }}>
                            <span style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.provider}</span>
                            <span style={{ ...monoMuted, textAlign: 'right' }}>{p.trades_opened}</span>
                            <span style={{ ...monoMuted, textAlign: 'right', color: p.open_count > 0 ? t.text : t.textFaint }}>{p.open_count}</span>
                            <span style={{ ...monoMuted, textAlign: 'right' }}>
                              <span style={{ color: t.positive }}>{p.wins}</span>
                              <span style={{ color: t.textFaint }}>/</span>
                              <span style={{ color: t.negative }}>{p.losses}</span>
                            </span>
                            <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 700, textAlign: 'right', color: realized >= 0 ? t.positive : t.negative }}>{fmtMoney(realized)}</span>
                            <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 700, textAlign: 'right', color: floating >= 0 ? t.positive : t.negative, opacity: p.open_count > 0 ? 1 : 0.4 }}>{fmtMoney(floating)}</span>
                            <span style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 800, textAlign: 'right', color: net >= 0 ? t.positive : t.negative }}>{fmtMoney(net)}</span>
                            <span style={{ fontSize: 11, fontFamily: monoFont, textAlign: 'right' }} title={mgmtFail > 0 ? `${mgmtFail} of ${mgmtTot} mgmt commands had no effect (scope mismatch / no positions)` : ''}>
                              {mgmtTot > 0 ? (
                                <span style={{ color: mgmtFail === mgmtTot && mgmtTot > 0 ? t.negative : mgmtFail > 0 ? '#f2c978' : t.positive }}>
                                  {mgmtTot - mgmtFail}/{mgmtTot}
                                </span>
                              ) : <span style={{ color: t.textFaint }}>—</span>}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>

          </div>
        )}

        {/* ═══ TRADES ═══ Open + Closed in one unified view */}
        {activeTab === 'history' && (
          <div className="tab-panel" key="history" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {unifiedTrades.length > 0 && (
              <div className="history-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
                {[
                  { label: 'Open', value: openCount, sub: `${fmtMoney(floatingSum)} floating`, color: t.text, subColor: floatingSum >= 0 ? t.positive : t.negative },
                  { label: 'Closed (30d)', value: closedCount, sub: `${closedWins}W / ${closedCount - closedWins}L`, color: t.text, subColor: t.textFaint },
                  { label: 'Realized P&L', value: fmtMoney(realizedSum), color: realizedSum >= 0 ? t.positive : t.negative },
                  { label: 'Net (visible)', value: fmtMoney(filteredPnLSum), sub: `${filteredTrades.length} row${filteredTrades.length !== 1 ? 's' : ''}`, color: filteredPnLSum >= 0 ? t.positive : t.negative, subColor: t.textFaint },
                ].map((s, i) => (
                  <div key={i} className="card-enter" style={{ ...glassCard, padding: '18px 22px', textAlign: 'center', animationDelay: `${i*0.05}s` }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: t.textFaint, marginBottom: 8 }}>{s.label}</div>
                    <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', color: s.color }}>{s.value}</div>
                    {s.sub && <div style={{ fontSize: 11, fontWeight: 600, color: s.subColor || t.textFaint, marginTop: 4 }}>{s.sub}</div>}
                  </div>
                ))}
              </div>
            )}

            {/* Quick-filter chips */}
            {(() => {
              const providerNames = [...new Set(unifiedTrades.map(tr => tr.provider).filter(Boolean))].slice(0, 5);
              const chips = [
                { id: 'all', label: 'All', active: filterStatus === 'all' && filterPnL === 'all' && !searchTerm,
                  apply: () => { setFilterStatus('all'); setFilterPnL('all'); setSearchTerm(''); } },
                { id: 'open', label: 'Open only', active: filterStatus === 'open',
                  apply: () => setFilterStatus(filterStatus === 'open' ? 'all' : 'open') },
                { id: 'losers', label: 'Losses', active: filterPnL === 'loss',
                  apply: () => setFilterPnL(filterPnL === 'loss' ? 'all' : 'loss') },
                { id: 'winners', label: 'Wins', active: filterPnL === 'win',
                  apply: () => setFilterPnL(filterPnL === 'win' ? 'all' : 'win') },
                ...providerNames.map(name => {
                  const short = name.length > 18 ? name.slice(0, 16) + '…' : name;
                  return { id: `prov-${name}`, label: short, active: searchTerm === name,
                    apply: () => setSearchTerm(searchTerm === name ? '' : name) };
                }),
              ];
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: -8 }}>
                  {chips.map(chip => (
                    <button key={chip.id} onClick={chip.apply} style={{
                      padding: '5px 12px', borderRadius: 999, cursor: 'pointer',
                      background: chip.active ? t.accentSoft || 'rgba(143,183,255,0.18)' : t.sectionBg,
                      border: `1px solid ${chip.active ? '#8fb7ff' : t.sectionBorder}`,
                      color: chip.active ? '#8fb7ff' : t.textMuted,
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                      transition: 'all 0.15s', whiteSpace: 'nowrap',
                    }}>{chip.label}</button>
                  ))}
                </div>
              );
            })()}

            <div className="card-enter" style={{ ...glassCard, padding: 24, animationDelay: '0.18s' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <History size={15} color={t.textMuted} />
                  <span style={{ fontWeight: 700, fontSize: 15 }}>Trades</span>
                  <span style={{ fontSize: 12, color: t.textFaint, background: t.countBg, border: `1px solid ${t.countBorder}`, borderRadius: 6, padding: '2px 8px' }}>{filteredTrades.length}</span>
                </div>
                <div className="dash-filter-bar" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ position: 'relative' }}>
                    <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: t.textFaint }} />
                    <input type="search" placeholder="Symbol / provider…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                      style={{ ...glassInput, paddingLeft: 32, width: 200, height: 34 }} />
                  </div>
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...glassInput, width: 110, height: 34 }}>
                    <option value="all" style={{ background: t.selectBg }}>All status</option>
                    <option value="open" style={{ background: t.selectBg }}>Open</option>
                    <option value="closed" style={{ background: t.selectBg }}>Closed</option>
                  </select>
                  <select value={filterAction} onChange={e => setFilterAction(e.target.value)} style={{ ...glassInput, width: 100, height: 34 }}>
                    <option value="all" style={{ background: t.selectBg }}>All sides</option>
                    <option value="BUY" style={{ background: t.selectBg }}>BUY</option>
                    <option value="SELL" style={{ background: t.selectBg }}>SELL</option>
                  </select>
                  <select value={filterPnL} onChange={e => setFilterPnL(e.target.value)} style={{ ...glassInput, width: 100, height: 34 }}>
                    <option value="all" style={{ background: t.selectBg }}>All P&L</option>
                    <option value="win" style={{ background: t.selectBg }}>Wins</option>
                    <option value="loss" style={{ background: t.selectBg }}>Losses</option>
                  </select>
                  <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...glassInput, width: 110, height: 34 }}>
                    <option value="time" style={{ background: t.selectBg }}>By Time</option>
                    <option value="profit" style={{ background: t.selectBg }}>By P&amp;L</option>
                    <option value="pair" style={{ background: t.selectBg }}>By Symbol</option>
                  </select>
                  <button style={btnIcon} onClick={loadHistory} title="Refresh"><RefreshCw size={14} /></button>
                  <button onClick={exportTrades} title="Export Excel"
                    style={{ background: t.primaryBg, border: `1px solid ${t.primaryBorder}`, borderRadius: 10, color: t.primaryText, fontWeight: 600, cursor: 'pointer', height: 34, padding: '0 14px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Download size={13} />Excel
                  </button>
                </div>
              </div>

              {filteredTrades.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: t.textFaint }}>
                  <History size={40} style={{ margin: '0 auto 12px', opacity: 0.25 }} />
                  <div style={{ fontSize: 13 }}>{isConnectedToMT5 ? 'No trades match the current filters' : 'Connect to MT5 to load trades'}</div>
                </div>
              ) : (
                <div className="hist-table-outer" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div className="hist-table-header-row" style={{ display: 'grid', gridTemplateColumns: '110px 90px 50px 50px 78px 70px 78px minmax(0, 1fr) 70px 84px 70px', gap: 8, padding: '0 16px 8px', ...colHeader }}>
                    <span>Time</span><span>Symbol</span><span>Side</span><span>Vol</span><span>Open</span><span>Current</span><span>SL / TP</span><span>Provider</span><span style={{ textAlign: 'center' }}>Status</span><span style={{ textAlign: 'right' }}>P&amp;L</span><span style={{ textAlign: 'right' }}>Action</span>
                  </div>
                  <div className="hist-table-scroll" style={{ maxHeight: 560, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {filteredTrades.map((tr, i) => {
                    const isOpen = tr.status === 'OPEN';
                    const isClosing = closingTickets.has(tr.ticket);
                    const rowBg = {
                      background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.72)',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                      borderRadius: 12,
                    };
                    return (
                      <div key={tr._key}
                        title={tr.original_message ? `Original signal:\n\n${tr.original_message}` : undefined}
                        style={{
                          ...rowBg, padding: '11px 16px', display: 'grid',
                          gridTemplateColumns: '110px 90px 50px 50px 78px 70px 78px minmax(0, 1fr) 70px 84px 70px',
                          gap: 8, alignItems: 'center',
                          animation: `slideUp 0.2s ease ${Math.min(i,10)*0.03}s both`,
                          opacity: isClosing ? 0.45 : 1,
                          cursor: tr.original_message ? 'help' : 'default',
                        }}>
                        <span style={{ fontSize: 12, fontFamily: monoFont, color: isDark ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.72)' }}>
                          {tr.time ? new Date(tr.time).toLocaleString('en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{tr.symbol}</span>
                          <span style={{ fontSize: 9, fontFamily: monoFont, color: t.textFaint }}>#{tr.ticket}</span>
                        </div>
                        <span style={{ ...badgeStyle, ...badge(tr.side) }}>{tr.side}</span>
                        <span style={{ fontSize: 11, fontFamily: monoFont, color: t.textMuted }}>{tr.volume}</span>
                        <span style={{ fontSize: 12, fontFamily: monoFont, color: t.textMuted }}>{tr.price_open?.toFixed(2)}</span>
                        <span style={{ fontSize: 12, fontFamily: monoFont, color: tr.price_current ? t.text : t.textFaint }}>
                          {tr.price_current ? tr.price_current.toFixed(2) : '—'}
                        </span>
                        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, fontFamily: monoFont, fontSize: 10 }}>
                          <span style={{ color: tr.sl ? t.negative : t.textFaint }}>{tr.sl ? tr.sl.toFixed(2) : '—'}</span>
                          <span style={{ color: tr.tp ? t.positive : t.textFaint }}>{tr.tp ? tr.tp.toFixed(2) : '—'}</span>
                        </span>
                        <span title={tr.provider || ''} style={{ fontSize: 12, color: tr.provider ? t.textMuted : t.textFaint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {tr.provider || '—'}
                        </span>
                        <span style={{
                          fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', padding: '3px 8px', borderRadius: 999,
                          background: isOpen ? 'rgba(74,222,128,0.14)' : 'rgba(148,163,184,0.14)',
                          border: `1px solid ${isOpen ? 'rgba(74,222,128,0.32)' : 'rgba(148,163,184,0.28)'}`,
                          color: isOpen ? '#4ade80' : t.textMuted,
                          textAlign: 'center', justifySelf: 'center',
                        }}>{tr.status}</span>
                        <span style={{ fontFamily: monoFont, fontWeight: 700, fontSize: 13, textAlign: 'right', color: tr.profit >= 0 ? t.positive : t.negative }}>
                          {fmtMoney(tr.profit)}
                        </span>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                          {isOpen && isAdmin ? (
                            <>
                              <button
                                onClick={() => handleBreakeven(tr.ticket)}
                                title={`Move SL to entry for #${tr.ticket}`}
                                style={{
                                  background: 'rgba(143,183,255,0.12)', border: '1px solid rgba(143,183,255,0.3)',
                                  color: '#8fb7ff', borderRadius: 8, padding: '4px 8px',
                                  fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                                  cursor: 'pointer',
                                }}>BE</button>
                              <button
                                onClick={() => handleClosePosition(tr.ticket)}
                                disabled={isClosing}
                                title={`Close #${tr.ticket}`}
                                style={{
                                  background: t.stopBg, border: `1px solid ${t.stopBorder}`,
                                  color: t.stopText, borderRadius: 8, padding: '4px 8px',
                                  fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                                  cursor: isClosing ? 'not-allowed' : 'pointer',
                                  display: 'inline-flex', alignItems: 'center', gap: 3,
                                }}>
                                <X size={10} />
                              </button>
                            </>
                          ) : (
                            <span style={{ color: t.textFaint, fontSize: 11 }}>—</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ ANALYTICS ═══ */}
        {activeTab === 'analytics' && (() => {
          // Heavy computations are scoped to this tab so the dashboard stays light.
          const allTrades = (tradeHistory || []).map(d => ({
            ...d,
            net: (d.profit || 0) + (d.commission || 0) + (d.swap || 0),
            timestamp: d.time ? new Date(d.time).getTime() : 0,
            hour: d.time ? new Date(d.time).getHours() : 0,
            dayOfWeek: d.time ? new Date(d.time).getDay() : 0,
            dateKey: d.time ? new Date(d.time).toISOString().slice(0, 10) : '',
          }));
          const wins = allTrades.filter(d => d.profit > 0);
          const losses = allTrades.filter(d => d.profit < 0);
          const totalNet = allTrades.reduce((s, d) => s + d.net, 0);
          const totalRealized = allTrades.reduce((s, d) => s + d.profit, 0);
          const avgWin = wins.length ? wins.reduce((s, d) => s + d.profit, 0) / wins.length : 0;
          const avgLoss = losses.length ? losses.reduce((s, d) => s + d.profit, 0) / losses.length : 0;
          const winRate = (wins.length + losses.length) ? (wins.length / (wins.length + losses.length)) * 100 : 0;
          const profitFactor = avgLoss !== 0 ? Math.abs(avgWin * wins.length / (avgLoss * losses.length)) : 0;
          const biggestWin = wins.reduce((m, d) => Math.max(m, d.profit), 0);
          const biggestLoss = losses.reduce((m, d) => Math.min(m, d.profit), 0);

          // Equity curve points (cumulative)
          const sortedByTime = [...allTrades].sort((a, b) => a.timestamp - b.timestamp);
          let running = 0;
          const equityPoints = sortedByTime.map((d, i) => {
            running += d.net;
            return {
              i, label: d.time ? new Date(d.time).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '',
              equity: parseFloat(running.toFixed(2)),
            };
          });
          // Max drawdown
          let peak = 0, maxDD = 0;
          equityPoints.forEach(p => {
            if (p.equity > peak) peak = p.equity;
            const dd = peak - p.equity;
            if (dd > maxDD) maxDD = dd;
          });

          // Per-provider performance
          const provGrouped = {};
          allTrades.forEach(d => {
            const p = d.provider || '—';
            if (!provGrouped[p]) provGrouped[p] = { name: p, trades: 0, wins: 0, losses: 0, total: 0 };
            provGrouped[p].trades += 1;
            provGrouped[p].total += d.net;
            if (d.profit > 0) provGrouped[p].wins += 1;
            if (d.profit < 0) provGrouped[p].losses += 1;
          });
          const providerStats = Object.values(provGrouped)
            .map(p => ({ ...p, wr: (p.wins + p.losses) ? (p.wins / (p.wins + p.losses)) * 100 : 0 }))
            .sort((a, b) => b.total - a.total);

          // Hour-of-day performance
          const hourStats = [...Array(24)].map((_, h) => ({
            hour: h, label: `${String(h).padStart(2, '0')}h`,
            count: 0, total: 0, wins: 0,
          }));
          allTrades.forEach(d => {
            const h = hourStats[d.hour];
            h.count += 1;
            h.total += d.net;
            if (d.profit > 0) h.wins += 1;
          });

          // Day-of-week
          const dowLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const dowStats = dowLabels.map((label, dow) => ({
            dow, label, count: 0, total: 0, wins: 0,
          }));
          allTrades.forEach(d => {
            const s = dowStats[d.dayOfWeek];
            s.count += 1;
            s.total += d.net;
            if (d.profit > 0) s.wins += 1;
          });

          // Win/Loss distribution for pie
          const outcomeDistribution = [
            { name: 'Wins', value: wins.length, color: '#10b981' },
            { name: 'Losses', value: losses.length, color: '#ef4444' },
            { name: 'Breakeven', value: allTrades.length - wins.length - losses.length, color: '#94a3b8' },
          ].filter(x => x.value > 0);

          const cardEnter = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } };
          const chartContentStyle = { background: t.cardBg, border: `1px solid ${t.cardBorder}`, borderRadius: 8, fontSize: 11 };

          return (
            <div className="tab-panel" key="analytics" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Hero metric tiles */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14 }} className="grid-mobile-2">
                {[
                  { label: 'Total Trades', value: allTrades.length, icon: Activity, accent: t.text },
                  { label: 'Win Rate', value: `${winRate.toFixed(1)}%`, icon: Target, accent: winRate >= 50 ? t.positive : t.negative },
                  { label: 'Realized P&L', value: fmtMoney(totalRealized), icon: DollarSign, accent: totalRealized >= 0 ? t.positive : t.negative },
                  { label: 'Profit Factor', value: profitFactor.toFixed(2), icon: Award, accent: profitFactor > 1 ? t.positive : t.negative },
                  { label: 'Max Drawdown', value: `-${ccySymbol}${maxDD.toFixed(2)}`, icon: Flame, accent: t.negative },
                ].map((m, i) => (
                  <motion.div key={m.label} {...cardEnter} transition={{ ...cardEnter.transition, delay: i * 0.06 }}
                    style={{ ...glassCard, padding: '18px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.textFaint }}>{m.label}</span>
                      <m.icon size={13} color={t.textMuted} />
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: m.accent, fontFamily: monoFont, letterSpacing: '-0.02em' }}>{m.value}</div>
                  </motion.div>
                ))}
              </div>

              {/* Secondary metrics row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }} className="grid-mobile-2">
                {[
                  { label: 'Avg Winner', value: fmtMoney(avgWin), accent: t.positive },
                  { label: 'Avg Loser', value: fmtMoney(avgLoss), accent: t.negative },
                  { label: 'Best Trade', value: fmtMoney(biggestWin), accent: t.positive },
                  { label: 'Worst Trade', value: fmtMoney(biggestLoss), accent: t.negative },
                ].map((m, i) => (
                  <motion.div key={m.label} {...cardEnter} transition={{ ...cardEnter.transition, delay: 0.3 + i * 0.05 }}
                    style={{ ...glassCard, padding: '14px 18px', textAlign: 'center' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.textFaint, marginBottom: 6 }}>{m.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: m.accent, fontFamily: monoFont }}>{m.value}</div>
                  </motion.div>
                ))}
              </div>

              {/* Equity curve full width */}
              <motion.div {...cardEnter} transition={{ ...cardEnter.transition, delay: 0.5 }} style={{ ...glassCard, padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <TrendingUp size={15} color={t.textMuted} />
                  <span style={{ fontWeight: 700, fontSize: 14 }}>Equity Progression — last 30 days</span>
                  <span style={{ fontSize: 11, color: totalNet >= 0 ? t.positive : t.negative, marginLeft: 8, fontWeight: 700 }}>
                    {fmtMoney(totalNet)} net
                  </span>
                </div>
                {equityPoints.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: t.textFaint, fontSize: 13 }}>No trade data yet</div>
                ) : (
                  <div style={{ height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={equityPoints}>
                        <defs>
                          <linearGradient id="equityFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={totalNet >= 0 ? '#10b981' : '#ef4444'} stopOpacity={0.45} />
                            <stop offset="100%" stopColor={totalNet >= 0 ? '#10b981' : '#ef4444'} stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={t.divider || 'rgba(148,163,184,0.18)'} vertical={false} />
                        <XAxis dataKey="label" tick={{ fill: t.textFaint, fontSize: 10 }} stroke={t.divider} />
                        <YAxis tick={{ fill: t.textFaint, fontSize: 10 }} stroke={t.divider} domain={['auto', 'auto']} />
                        <Tooltip contentStyle={chartContentStyle} labelStyle={{ color: t.text }} itemStyle={{ color: t.text }}
                          formatter={(v) => [`${ccySymbol}${Number(v).toFixed(2)}`, 'Equity']} />
                        <Area type="monotone" dataKey="equity" stroke={totalNet >= 0 ? '#10b981' : '#ef4444'} strokeWidth={2} fill="url(#equityFill)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </motion.div>

              {/* Two-column: Provider performance + Win/Loss pie */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }} className="grid-mobile-1">
                <motion.div {...cardEnter} transition={{ ...cardEnter.transition, delay: 0.6 }} style={{ ...glassCard, padding: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <BarChart3 size={15} color={t.textMuted} />
                    <span style={{ fontWeight: 700, fontSize: 14 }}>P&amp;L per Provider</span>
                  </div>
                  {providerStats.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: t.textFaint, fontSize: 13 }}>No data</div>
                  ) : (
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={providerStats} layout="vertical" margin={{ left: 100 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={t.divider} horizontal={false} />
                          <XAxis type="number" tick={{ fill: t.textFaint, fontSize: 10 }} stroke={t.divider} />
                          <YAxis type="category" dataKey="name" tick={{ fill: t.textFaint, fontSize: 11 }} stroke={t.divider} width={100}
                            tickFormatter={n => n.length > 14 ? n.slice(0, 13) + '…' : n} />
                          <Tooltip contentStyle={chartContentStyle} labelStyle={{ color: t.text }} itemStyle={{ color: t.text }}
                            formatter={(v) => [`${ccySymbol}${Number(v).toFixed(2)}`, 'P&L']} />
                          <Bar dataKey="total" radius={[0, 4, 4, 0]} animationDuration={800}>
                            {providerStats.map((p, i) => (
                              <Cell key={i} fill={p.total >= 0 ? '#10b981' : '#ef4444'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </motion.div>

                <motion.div {...cardEnter} transition={{ ...cardEnter.transition, delay: 0.65 }} style={{ ...glassCard, padding: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <PieChart size={15} color={t.textMuted} />
                    <span style={{ fontWeight: 700, fontSize: 14 }}>Outcome</span>
                  </div>
                  {outcomeDistribution.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: t.textFaint, fontSize: 13 }}>No data</div>
                  ) : (
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie data={outcomeDistribution} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={90}
                            paddingAngle={3} animationDuration={800}>
                            {outcomeDistribution.map((entry, i) => (
                              <Cell key={i} fill={entry.color} stroke="none" />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={chartContentStyle} labelStyle={{ color: t.text }} itemStyle={{ color: t.text }} />
                          <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11, color: t.textMuted }} />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </motion.div>
              </div>

              {/* Two-column: Hour heatmap + Day-of-week */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }} className="grid-mobile-1">
                <motion.div {...cardEnter} transition={{ ...cardEnter.transition, delay: 0.7 }} style={{ ...glassCard, padding: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <Clock size={15} color={t.textMuted} />
                    <span style={{ fontWeight: 700, fontSize: 14 }}>Performance by Hour (UTC+0)</span>
                  </div>
                  <div style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hourStats}>
                        <CartesianGrid strokeDasharray="3 3" stroke={t.divider} vertical={false} />
                        <XAxis dataKey="label" tick={{ fill: t.textFaint, fontSize: 9 }} stroke={t.divider} interval={1} />
                        <YAxis tick={{ fill: t.textFaint, fontSize: 10 }} stroke={t.divider} />
                        <Tooltip contentStyle={chartContentStyle} labelStyle={{ color: t.text }} itemStyle={{ color: t.text }}
                          formatter={(v, k, item) => {
                            if (k === 'total') return [`${ccySymbol}${Number(v).toFixed(2)}`, 'P&L'];
                            return [v, k];
                          }} />
                        <Bar dataKey="total" radius={[4, 4, 0, 0]} animationDuration={800}>
                          {hourStats.map((h, i) => (
                            <Cell key={i} fill={h.total >= 0 ? '#10b981' : '#ef4444'} opacity={h.count > 0 ? 0.85 : 0.2} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </motion.div>

                <motion.div {...cardEnter} transition={{ ...cardEnter.transition, delay: 0.75 }} style={{ ...glassCard, padding: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <Calendar size={15} color={t.textMuted} />
                    <span style={{ fontWeight: 700, fontSize: 14 }}>Day of Week</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {dowStats.map((d, idx) => {
                      const maxAbs = Math.max(...dowStats.map(x => Math.abs(x.total)), 1);
                      const widthPct = (Math.abs(d.total) / maxAbs) * 100;
                      return (
                        <motion.div key={d.dow} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: 0.8 + idx * 0.05 }}
                          style={{ display: 'grid', gridTemplateColumns: '40px 1fr 70px', gap: 8, alignItems: 'center' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: t.textMuted }}>{d.label}</span>
                          <div style={{ height: 14, background: t.sectionBg, borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                            <div style={{
                              height: '100%', width: `${widthPct}%`, transition: 'width 0.6s ease',
                              background: d.total >= 0 ? '#10b981' : '#ef4444', opacity: d.count > 0 ? 0.7 : 0.15,
                            }} />
                          </div>
                          <span style={{ fontSize: 11, fontFamily: monoFont, fontWeight: 700, color: d.total >= 0 ? t.positive : t.negative, textAlign: 'right' }}>
                            {fmtMoney(d.total, { decimals: 0 })}
                          </span>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              </div>

              {/* Provider detail table */}
              <motion.div {...cardEnter} transition={{ ...cardEnter.transition, delay: 0.85 }} style={{ ...glassCard, padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <Award size={15} color={t.textMuted} />
                  <span style={{ fontWeight: 700, fontSize: 14 }}>Provider Leaderboard</span>
                </div>
                {providerStats.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: t.textFaint, fontSize: 13 }}>No data</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 80px 90px 90px 90px', gap: 12, padding: '0 14px 6px', ...colHeader }}>
                      <span>#</span><span>Provider</span><span style={{ textAlign: 'right' }}>Trades</span>
                      <span style={{ textAlign: 'right' }}>W/L</span>
                      <span style={{ textAlign: 'right' }}>Win Rate</span>
                      <span style={{ textAlign: 'right' }}>Net P&amp;L</span>
                    </div>
                    {providerStats.map((p, idx) => (
                      <motion.div key={p.name} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.9 + idx * 0.04 }}
                        style={{ display: 'grid', gridTemplateColumns: '36px 1fr 80px 90px 90px 90px', gap: 12, padding: '10px 14px',
                          background: t.sectionBg, border: `1px solid ${t.sectionBorder}`, borderRadius: 10, alignItems: 'center' }}>
                        <span style={{ fontSize: 11, fontFamily: monoFont, fontWeight: 700,
                          color: idx === 0 ? '#fbbf24' : idx === 1 ? '#94a3b8' : idx === 2 ? '#f59e0b' : t.textFaint }}>
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.name}
                        </span>
                        <span style={{ fontSize: 12, fontFamily: monoFont, color: t.textMuted, textAlign: 'right' }}>{p.trades}</span>
                        <span style={{ fontSize: 12, fontFamily: monoFont, textAlign: 'right' }}>
                          <span style={{ color: t.positive }}>{p.wins}</span>
                          <span style={{ color: t.textFaint }}> / </span>
                          <span style={{ color: t.negative }}>{p.losses}</span>
                        </span>
                        <span style={{ fontSize: 12, fontFamily: monoFont, color: p.wr >= 50 ? t.positive : t.negative, textAlign: 'right' }}>
                          {p.wr.toFixed(1)}%
                        </span>
                        <span style={{ fontSize: 13, fontFamily: monoFont, fontWeight: 800, color: p.total >= 0 ? t.positive : t.negative, textAlign: 'right' }}>
                          {fmtMoney(p.total)}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
          );
        })()}

        {/* ═══ JOURNAL ═══ */}
        {activeTab === 'journal' && (
          <div className="tab-panel" key="journal" style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 900 }}>
            {/* New Entry */}
            <div className="card-enter" style={{ ...glassCard, padding: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <BookOpen size={15} color={t.textMuted} />
                <span style={{ fontWeight: 700, fontSize: 15 }}>New Journal Entry</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }} className="grid-mobile-1">
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Pair</label>
                  <input style={glassInput} value={journalDraft.pair} onChange={e => setJournalDraft(p => ({...p, pair: e.target.value}))} placeholder="XAUUSD" />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Direction</label>
                  <select style={{ ...glassInput, cursor: 'pointer' }} value={journalDraft.direction} onChange={e => setJournalDraft(p => ({...p, direction: e.target.value}))}>
                    <option value="BUY" style={{ background: t.selectBg }}>BUY</option>
                    <option value="SELL" style={{ background: t.selectBg }}>SELL</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Outcome ($)</label>
                  <input style={glassInput} value={journalDraft.outcome} onChange={e => setJournalDraft(p => ({...p, outcome: e.target.value}))} placeholder="+25.50" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 14, marginBottom: 14 }} className="grid-mobile-1">
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Notes</label>
                  <textarea style={{ ...glassInput, minHeight: 70, resize: 'vertical' }} value={journalDraft.notes} onChange={e => setJournalDraft(p => ({...p, notes: e.target.value}))} placeholder="What was your reasoning? What did you learn?" />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Emotion</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {['confident', 'neutral', 'fearful', 'fomo'].map(em => (
                      <button key={em} onClick={() => setJournalDraft(p => ({...p, emotion: em}))}
                        style={{
                          padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                          background: journalDraft.emotion === em ? t.primaryBg : t.sectionBg,
                          border: `1px solid ${journalDraft.emotion === em ? t.primaryBorder : t.sectionBorder}`,
                          color: journalDraft.emotion === em ? t.primaryText : t.textDim,
                          textTransform: 'capitalize',
                        }}>
                        {em}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <button onClick={() => {
                if (!journalDraft.pair) return;
                const entry = { ...journalDraft, id: Date.now(), date: new Date().toISOString() };
                const updated = [entry, ...journalEntries];
                setJournalEntries(updated);
                localStorage.setItem('xceller_journal', JSON.stringify(updated));
                setJournalDraft({ pair: '', direction: 'BUY', notes: '', emotion: 'neutral', outcome: '' });
              }} style={{
                background: t.primaryBg, border: `1px solid ${t.primaryBorder}`, borderRadius: 10,
                color: t.primaryText, fontWeight: 600, cursor: 'pointer', padding: '10px 24px', fontSize: 13,
              }}>
                Save Entry
              </button>
            </div>

            {/* Entries list */}
            <div className="card-enter" style={{ ...glassCard, padding: 24, animationDelay: '0.1s' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>Past Entries</span>
                <span style={{ fontSize: 12, color: t.textFaint, background: t.countBg, border: `1px solid ${t.countBorder}`, borderRadius: 6, padding: '2px 8px' }}>
                  {journalEntries.length}
                </span>
              </div>
              {journalEntries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: t.textFaint }}>
                  <BookOpen size={36} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
                  <div style={{ fontSize: 13 }}>No journal entries yet. Start documenting your trades!</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 480, overflowY: 'auto' }}>
                  {journalEntries.map((entry, i) => {
                    const pnl = parseFloat(entry.outcome || 0);
                    return (
                      <div key={entry.id} style={{ ...glassRow, padding: '14px 18px', animation: `slideUp 0.2s ease ${Math.min(i, 8) * 0.04}s both` }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ ...badgeStyle, ...badge(entry.direction) }}>{entry.direction}</span>
                            <span style={{ fontWeight: 700, fontSize: 14 }}>{entry.pair}</span>
                            <span style={{ fontSize: 10, color: t.textFaint, padding: '1px 6px', background: t.sectionBg, border: `1px solid ${t.sectionBorder}`, borderRadius: 4, textTransform: 'capitalize' }}>{entry.emotion}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: pnl >= 0 ? t.positive : t.negative }}>{pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}</span>
                            <button onClick={() => {
                              const updated = journalEntries.filter(e => e.id !== entry.id);
                              setJournalEntries(updated);
                              localStorage.setItem('xceller_journal', JSON.stringify(updated));
                            }} style={{ background: 'none', border: 'none', color: t.textFaint, cursor: 'pointer', fontSize: 11, padding: 2 }}>x</button>
                          </div>
                        </div>
                        {entry.notes && <p style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5 }}>{entry.notes}</p>}
                        <div style={{ fontSize: 10, color: t.textFaint, marginTop: 6, fontFamily: 'monospace' }}>
                          {new Date(entry.date).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ CONFIG ═══ */}
        {activeTab === 'audit' && (
          <div className="tab-panel" key="audit" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="card-enter" style={{ ...glassCard, padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: t.text }}>Signal Audit</div>
                  <div style={{ fontSize: 12, color: t.textDim, marginTop: 4 }}>Per dag terugkijken welke signalen zijn gevonden, aangepast, uitgevoerd of geskipt.</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="date" value={auditDate} onChange={e => setAuditDate(e.target.value)} style={{ ...glassInput, width: 170, height: 38 }} />
                  <button onClick={() => loadAuditEntries(auditDate)} style={{ ...btnIcon, minWidth: 38, height: 38 }}><RefreshCw size={14} /></button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }} className="grid-mobile-2">
                {[
                  { label: 'Entries', value: auditEntries.length },
                  { label: 'Parsed', value: auditSummary.parsed_signal || 0 },
                  { label: 'Executed', value: auditSummary['status:executed'] || 0 },
                  { label: 'Skipped/Failed', value: (auditSummary['status:skipped'] || 0) + (auditSummary['status:failed'] || 0) + (auditSummary['status:exception'] || 0) },
                ].map(item => (
                  <div key={item.label} style={{ ...glassRow, padding: '16px 18px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: t.textFaint, marginBottom: 8 }}>{item.label}</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: t.text }}>{item.value}</div>
                  </div>
                ))}
              </div>

              {auditLoading ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: t.textDim }}>Loading audit log...</div>
              ) : auditEntries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: t.textDim }}>Geen audit entries gevonden voor {auditDate}.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 680, overflowY: 'auto' }}>
                  {auditEntries.map((entry, index) => {
                    const parsedSignal = entry.signal || (Array.isArray(entry.signals) ? entry.signals[0] : null);
                    const sourceLabel = entry.channel_profile?.label || entry.source || 'Unknown';
                    const status = entry.status || 'logged';
                    return (
                      <div key={`${entry.timestamp || auditDate}-${index}`} style={{ ...glassRow, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ ...badgeStyle, background: t.primaryBg, border: `1px solid ${t.primaryBorder}`, color: t.primaryText }}>{entry.kind || 'entry'}</span>
                            <span style={{ ...badgeStyle, background: status === 'executed' ? t.badgeBuyBg : t.badgeClosedBg, border: `1px solid ${status === 'executed' ? t.badgeBuyBorder : t.badgeClosedBorder}`, color: status === 'executed' ? t.badgeBuyText : t.badgeClosedText }}>{status}</span>
                            <span style={{ fontSize: 12, color: t.textDim }}>{sourceLabel}</span>
                          </div>
                          <span style={{ fontSize: 11, fontFamily: 'monospace', color: t.textFaint }}>{entry.timestamp ? new Date(entry.timestamp).toLocaleString('nl-NL') : auditDate}</span>
                        </div>

                        {entry.message_text && (
                          <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5, background: t.sectionBg, border: `1px solid ${t.sectionBorder}`, borderRadius: 10, padding: '10px 12px' }}>
                            {entry.message_text}
                          </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }} className="grid-mobile-1">
                          <div style={{ fontSize: 12, color: t.textDim }}><strong style={{ color: t.text }}>Symbol:</strong> {parsedSignal?.symbol || entry.result?.symbol || '-'}</div>
                          <div style={{ fontSize: 12, color: t.textDim }}><strong style={{ color: t.text }}>Action:</strong> {parsedSignal?.action || entry.command?.type || '-'}</div>
                          <div style={{ fontSize: 12, color: t.textDim }}><strong style={{ color: t.text }}>SL mode:</strong> {entry.effective_trading?.stop_loss_mode || entry.channel_profile?.stop_loss_mode || config.trading.stopLossMode}</div>
                        </div>

                        {(parsedSignal || entry.command || entry.result) && (
                          <pre style={{ margin: 0, padding: '10px 12px', borderRadius: 10, background: t.sectionBg, border: `1px solid ${t.sectionBorder}`, color: t.textDim, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {JSON.stringify({ signal: parsedSignal, command: entry.command, result: entry.result }, null, 2)}
                          </pre>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'config' && (
          <div className="tab-panel" key="config" style={{ maxWidth: 920, display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="card-enter" style={{ ...glassCard, padding: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
                <Settings size={16} color={t.textMuted} />
                <span style={{ fontWeight: 700, fontSize: 16 }}>Configuration</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div style={{ background: t.sectionBg, border: `1px solid ${t.sectionBorder}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: t.textMuted, marginBottom: 16 }}>Telegram API</div>
                  <div className="grid-mobile-1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>API ID</label>
                      <div style={{ position: 'relative' }}>
                        <input style={{ ...glassInput, paddingRight: 36 }} type={showPasswordFields.apiId ? 'text' : 'password'} value={config.telegram.apiId} onChange={e => setConfig(p => ({...p, telegram: {...p.telegram, apiId: e.target.value}}))} placeholder="12345678" />
                        <button onClick={() => setShowPasswordFields(p => ({...p, apiId: !p.apiId}))} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: t.textDim }}>
                          {showPasswordFields.apiId ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>API Hash</label>
                      <div style={{ position: 'relative' }}>
                        <input style={{ ...glassInput, paddingRight: 36 }} type={showPasswordFields.apiHash ? 'text' : 'password'} value={config.telegram.apiHash} onChange={e => setConfig(p => ({...p, telegram: {...p.telegram, apiHash: e.target.value}}))} placeholder="abc123…" />
                        <button onClick={() => setShowPasswordFields(p => ({...p, apiHash: !p.apiHash}))} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: t.textDim }}>
                          {showPasswordFields.apiHash ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim }}>Telegram Channels</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button onClick={loadTelegramChats} style={{ background: 'none', border: 'none', color: t.primaryText, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                          {telegramChatsLoading ? 'Loading chats...' : 'Browse joined chats'}
                        </button>
                        <button onClick={addTelegramChannel} style={{ background: 'none', border: 'none', color: t.primaryText, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                          + Add channel
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {(config.telegram.channelNames || ['']).map((channel, index) => (
                        <div key={index} style={{ display: 'grid', gridTemplateColumns: telegramChats.length ? '1fr 240px auto' : '1fr auto', gap: 10 }}>
                          <input
                            style={glassInput}
                            value={channel}
                            onChange={e => updateTelegramChannel(index, e.target.value)}
                            placeholder={index === 0 ? '@channel or -100xxxxxxxxxx' : 'Extra channel / group ID'}
                          />
                          {telegramChats.length > 0 && (
                            <select
                              value=""
                              onChange={e => {
                                if (e.target.value) {
                                  selectTelegramChat(index, e.target.value);
                                }
                              }}
                              style={{ ...glassInput, cursor: 'pointer' }}
                            >
                              <option value="" style={{ background: t.selectBg }}>Pick from joined chats</option>
                              {telegramChats.map(chat => (
                                <option key={`${chat.type}-${chat.id}`} value={chat.access_value} style={{ background: t.selectBg }}>
                                  {chat.title}{chat.username ? ` (${chat.username})` : ` (${chat.id})`}
                                </option>
                              ))}
                            </select>
                          )}
                          <button
                            onClick={() => removeTelegramChannel(index)}
                            disabled={(config.telegram.channelNames || []).length <= 1}
                            style={{
                              minWidth: 44,
                              borderRadius: 10,
                              border: `1px solid ${t.sectionBorder}`,
                              background: t.sectionBg,
                              color: (config.telegram.channelNames || []).length <= 1 ? t.textFaint : t.negative,
                              cursor: (config.telegram.channelNames || []).length <= 1 ? 'not-allowed' : 'pointer',
                              opacity: (config.telegram.channelNames || []).length <= 1 ? 0.5 : 1,
                            }}
                          >
                            X
                          </button>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 11, color: t.textDim, marginTop: 8 }}>
                      xceller luistert naar alle kanalen hierboven. Het eerste kanaal blijft de primaire fallback voor oudere config.
                    </div>
                    {telegramChats.length > 0 && (
                      <div style={{ fontSize: 11, color: t.textDim, marginTop: 6 }}>
                        Showing {telegramChats.length} chats from your current Telegram session.
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 18, paddingTop: 18, borderTop: `1px solid ${t.sectionBorder}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim }}>Per-channel Profielen</label>
                      <button onClick={addChannelProfile} style={{ background: 'none', border: 'none', color: t.primaryText, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                        + Add profile
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {(config.telegram.channelProfiles || []).length === 0 && (
                        <div style={{ fontSize: 11, color: t.textDim }}>Geen overrides ingesteld. Zonder profiel gebruikt elk kanaal de globale trading settings hieronder.</div>
                      )}
                      {(config.telegram.channelProfiles || []).map((profile, index) => (
                        <div key={index} style={{ background: t.rowBg, border: `1px solid ${t.rowBorder}`, borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr auto', gap: 10 }} className="grid-mobile-1">
                            <input style={glassInput} value={profile.channel || ''} onChange={e => updateChannelProfile(index, 'channel', e.target.value)} placeholder="@channel / group title / -100chatid" />
                            <input style={glassInput} value={profile.label || ''} onChange={e => updateChannelProfile(index, 'label', e.target.value)} placeholder="Friendly label" />
                            <button onClick={() => removeChannelProfile(index)} style={{ minWidth: 44, borderRadius: 10, border: `1px solid ${t.sectionBorder}`, background: t.sectionBg, color: t.negative, cursor: 'pointer' }}>X</button>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }} className="grid-mobile-2">
                            <input style={glassInput} value={profile.lot_size ?? ''} onChange={e => updateChannelProfile(index, 'lot_size', e.target.value)} placeholder="Lot override" />
                            <input style={glassInput} value={profile.risk_per_trade ?? ''} onChange={e => updateChannelProfile(index, 'risk_per_trade', e.target.value)} placeholder="Risk %" />
                            <select style={{ ...glassInput, cursor: 'pointer' }} value={profile.stop_loss_mode || ''} onChange={e => updateChannelProfile(index, 'stop_loss_mode', e.target.value)}>
                              <option value="" style={{ background: t.selectBg }}>Use global SL mode</option>
                              <option value="signal" style={{ background: t.selectBg }}>Signal SL</option>
                              <option value="risk_percentage" style={{ background: t.selectBg }}>Dashboard risk %</option>
                            </select>
                            <select style={{ ...glassInput, cursor: 'pointer' }} value={profile.enforce_entry_range === '' || profile.enforce_entry_range == null ? '' : String(profile.enforce_entry_range)} onChange={e => updateChannelProfile(index, 'enforce_entry_range', e.target.value === '' ? '' : e.target.value === 'true')}>
                              <option value="" style={{ background: t.selectBg }}>Use global range rule</option>
                              <option value="true" style={{ background: t.selectBg }}>Range enforced</option>
                              <option value="false" style={{ background: t.selectBg }}>Ignore range</option>
                            </select>
                          </div>

                          <button onClick={() => updateChannelProfile(index, 'enabled', profile.enabled === false)} style={{
                            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                            padding: '10px 14px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            background: profile.enabled !== false ? t.badgeBuyBg : t.sectionBg,
                            border: `1px solid ${profile.enabled !== false ? t.badgeBuyBorder : t.sectionBorder}`,
                            color: profile.enabled !== false ? t.badgeBuyText : t.textDim,
                          }}>
                            <div style={{ width: 32, height: 18, borderRadius: 9, position: 'relative', background: profile.enabled !== false ? t.positive : t.sectionBorder, transition: 'all 0.2s' }}>
                              <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: profile.enabled !== false ? 16 : 2, transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                            </div>
                            {profile.enabled !== false ? 'Profile enabled' : 'Profile paused'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ background: t.sectionBg, border: `1px solid ${t.sectionBorder}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: t.textMuted, marginBottom: 16 }}>MetaTrader 5</div>
                  <div className="grid-mobile-1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Login</label>
                      <div style={{ position: 'relative' }}>
                        <input style={{ ...glassInput, paddingRight: 36 }} type={showPasswordFields.mt5Login ? 'text' : 'password'} value={config.mt5.login} onChange={e => setConfig(p => ({...p, mt5: {...p.mt5, login: e.target.value}}))} placeholder="12345678" />
                        <button onClick={() => setShowPasswordFields(p => ({...p, mt5Login: !p.mt5Login}))} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: t.textDim }}>
                          {showPasswordFields.mt5Login ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Password</label>
                      <div style={{ position: 'relative' }}>
                        <input style={{ ...glassInput, paddingRight: 36 }} type={showPasswordFields.mt5Password ? 'text' : 'password'} value={config.mt5.password} onChange={e => setConfig(p => ({...p, mt5: {...p.mt5, password: e.target.value}}))} placeholder="MT5 password" />
                        <button onClick={() => setShowPasswordFields(p => ({...p, mt5Password: !p.mt5Password}))} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: t.textDim }}>
                          {showPasswordFields.mt5Password ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="grid-mobile-1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 14 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Server</label>
                      <input style={glassInput} value={config.mt5.server} onChange={e => setConfig(p => ({...p, mt5: {...p.mt5, server: e.target.value}}))} placeholder="YourBroker-Demo" />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Lot Size</label>
                      <input style={glassInput} value={config.trading.lotSize} onChange={e => setConfig(p => ({...p, trading: {...p.trading, lotSize: e.target.value}}))} placeholder="0.01" />
                    </div>
                  </div>
                </div>
                {/* TP Orders per level */}
                <div style={{ background: t.sectionBg, border: `1px solid ${t.sectionBorder}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: t.textMuted, marginBottom: 6 }}>Orders per Take Profit</div>
                  <div style={{ fontSize: 11, color: t.textDim, marginBottom: 16 }}>Choose how many orders to place for each TP level (1-5)</div>
                  <div className="grid-mobile-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
                    {[1, 2, 3].map(tp => (
                      <div key={tp}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>TP{tp} Orders</label>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {[1, 2, 3, 4, 5].map(n => (
                            <button key={n} onClick={() => setConfig(p => ({
                              ...p, trading: { ...p.trading, tpOrders: { ...p.trading.tpOrders, [`tp${tp}`]: n } }
                            }))}
                              style={{
                                flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                transition: 'all 0.2s',
                                background: config.trading.tpOrders[`tp${tp}`] === n
                                  ? iri : t.inputBg,
                                border: `1px solid ${config.trading.tpOrders[`tp${tp}`] === n
                                  ? 'rgba(143,183,255,0.42)' : t.inputBorder}`,
                                color: config.trading.tpOrders[`tp${tp}`] === n ? '#fff' : t.textDim,
                                boxShadow: config.trading.tpOrders[`tp${tp}`] === n
                                  ? '0 12px 28px rgba(111,168,255,0.22)' : 'none',
                              }}>
                              {n}x
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Auto Break-even toggle */}
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${t.sectionBorder}` }}>
                    <button onClick={() => setConfig(p => ({
                      ...p, trading: { ...p.trading, autoBreakeven: !p.trading.autoBreakeven }
                    }))}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                        padding: '10px 14px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                        background: config.trading.autoBreakeven ? (isDark ? 'rgba(52,211,153,0.08)' : 'rgba(52,211,153,0.06)') : t.sectionBg,
                        border: `1px solid ${config.trading.autoBreakeven ? 'rgba(52,211,153,0.2)' : t.sectionBorder}`,
                        color: config.trading.autoBreakeven ? t.positive : t.textDim,
                      }}>
                      <div style={{
                        width: 32, height: 18, borderRadius: 9, position: 'relative',
                        background: config.trading.autoBreakeven ? t.positive : t.sectionBorder, transition: 'all 0.2s',
                      }}>
                        <div style={{
                          width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2,
                          left: config.trading.autoBreakeven ? 16 : 2, transition: 'all 0.2s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }} />
                      </div>
                      Auto Break-even
                    </button>
                    <div style={{ fontSize: 11, color: t.textDim, marginTop: 6, paddingLeft: 42 }}>When the signal provider says "set breakeven", automatically move SL to entry price</div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <button onClick={handleSaveConfig} disabled={isLoading}
                    style={{ background: t.primaryBg, border: `1px solid ${t.primaryBorder}`, borderRadius: 10, color: t.primaryText, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', padding: '10px 28px', fontSize: 13, opacity: isLoading ? 0.6 : 1 }}>
                    {isLoading ? 'Saving…' : 'Save Configuration'}
                  </button>
                  <span style={{ fontSize: 12, color: t.textFaint }}>Demo account recommended for testing</span>
                </div>
              </div>
            </div>

            {/* Risk Management */}
            <div className="card-enter" style={{ ...glassCard, padding: 32, animationDelay: '0.1s' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
                <Shield size={16} color={t.textMuted} />
                <span style={{ fontWeight: 700, fontSize: 16 }}>Risk Management</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div className="grid-mobile-1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Max Daily Loss ($)</label>
                    <input style={glassInput} value={config.trading.maxDailyLoss} onChange={e => setConfig(p => ({...p, trading: {...p.trading, maxDailyLoss: e.target.value}}))} placeholder="100" />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Max Open Positions</label>
                    <input style={glassInput} value={config.trading.maxPositions} onChange={e => setConfig(p => ({...p, trading: {...p.trading, maxPositions: e.target.value}}))} placeholder="20" />
                  </div>
                </div>
                <div style={{ background: t.sectionBg, border: `1px solid ${t.sectionBorder}`, borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: t.text }}>Max TP Distance</div>
                      <div style={{ fontSize: 11, color: t.textDim, marginTop: 2 }}>Cap unrealistic TPs — if a TP is farther than this many pips from entry, it's automatically moved to the limit. Leave empty to disable.</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <input
                        style={{ ...glassInput, width: 90, textAlign: 'center' }}
                        type="number"
                        min="1"
                        placeholder="off"
                        value={config.trading.maxTpPips}
                        onChange={e => setConfig(p => ({ ...p, trading: { ...p.trading, maxTpPips: e.target.value } }))}
                      />
                      <span style={{ fontSize: 11, color: t.textFaint, whiteSpace: 'nowrap' }}>pips</span>
                    </div>
                  </div>
                  {config.trading.maxTpPips && (
                    <div style={{ fontSize: 11, color: t.positive, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: t.positive }} />
                      TPs farther than {config.trading.maxTpPips} pips from entry will be capped (e.g. XAUUSD: {(parseFloat(config.trading.maxTpPips) * 0.1).toFixed(1)} price units)
                    </div>
                  )}
                </div>
                <div className="grid-mobile-1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Risk per Trade (%)</label>
                    <input style={glassInput} value={config.trading.riskPerTrade} onChange={e => setConfig(p => ({...p, trading: {...p.trading, riskPerTrade: e.target.value}}))} placeholder="2" />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Stop Loss Source</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[
                        { value: 'signal', label: 'Use signal SL' },
                        { value: 'risk_percentage', label: 'Use dashboard risk %' },
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => setConfig(p => ({...p, trading: {...p.trading, stopLossMode: option.value}}))}
                          style={{
                            flex: 1,
                            padding: '10px 12px',
                            borderRadius: 10,
                            border: `1px solid ${config.trading.stopLossMode === option.value ? t.primaryBorder : t.sectionBorder}`,
                            background: config.trading.stopLossMode === option.value ? t.primaryBg : t.sectionBg,
                            color: config.trading.stopLossMode === option.value ? t.primaryText : t.textDim,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'end' }}>
                  <button onClick={() => setConfig(p => ({...p, trading: {...p.trading, trailingStop: !p.trading.trailingStop}}))}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                      padding: '10px 14px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                      background: config.trading.trailingStop ? t.badgeBuyBg : t.sectionBg,
                      border: `1px solid ${config.trading.trailingStop ? t.badgeBuyBorder : t.sectionBorder}`,
                      color: config.trading.trailingStop ? t.badgeBuyText : t.textDim,
                    }}>
                    <div style={{
                      width: 32, height: 18, borderRadius: 9, position: 'relative',
                      background: config.trading.trailingStop ? t.positive : t.sectionBorder, transition: 'all 0.2s',
                    }}>
                      <div style={{
                        width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2,
                        left: config.trading.trailingStop ? 16 : 2, transition: 'all 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </div>
                    Trailing Stop
                  </button>
                </div>

                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  background: isDark ? 'rgba(251,191,36,0.06)' : 'rgba(251,191,36,0.05)',
                  border: `1px solid ${isDark ? 'rgba(251,191,36,0.12)' : 'rgba(251,191,36,0.10)'}`,
                  borderRadius: 12, padding: '12px 14px', fontSize: 12, color: t.textMuted,
                }}>
                  <AlertTriangle size={14} color="#fbbf24" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>Riskinstellingen worden samen met de backend-config opgeslagen. Bij dashboard risk berekent xceller de SL automatisch op basis van je risk per trade.</span>
                </div>

                <button onClick={handleSaveConfig} disabled={isLoading} style={{
                  background: t.primaryBg, border: `1px solid ${t.primaryBorder}`, borderRadius: 10,
                  color: t.primaryText, fontWeight: 600, cursor: 'pointer', padding: '10px 28px', fontSize: 13,
                  alignSelf: 'flex-start', opacity: isLoading ? 0.6 : 1,
                }}>
                  {isLoading ? 'Saving…' : 'Save All Settings'}
                </button>

              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
